/**
 * =========================================================================
 * My Magnetic Stories Personalizer - BUNDLED
 * All component classes are included in this single file to avoid
 * module loading issues in the Shopify theme environment.
 *
 * Bundle Order:
 * 1. Store
 * 2. FabricWrapper
 * 3. ToolPanel
 * 4. EditingWorkspace
 * 5. MagnetCard
 * 6. PersonalizerApp (Main)
 * =========================================================================
 */

// =========================================================================
// Logging Utility - Production vs Development Mode
// =========================================================================
const MMS_LOGGER = {
  // Set to true for development/debugging, false for production
  isDevelopmentMode: false,
  
  // Log levels
  LEVELS: {
    DEBUG: 0,
    INFO: 1,
    WARN: 2,
    ERROR: 3
  },
  
  // Current log level (defaults to WARN for production)
  currentLevel: 2,
  
  // Set development mode (enables all logging)
  enableDevelopmentMode() {
    this.isDevelopmentMode = true;
    this.currentLevel = this.LEVELS.DEBUG;
    console.log('🟢 MMS Personalizer DEBUG VERSION LOADED');
  },
  
  // Set production mode (warnings and errors only)
  enableProductionMode() {
    this.isDevelopmentMode = false;
    this.currentLevel = this.LEVELS.WARN;
    console.log('🚀 MMS Personalizer PRODUCTION VERSION LOADED');
  },
  
  // Set custom log level
  setLogLevel(level) {
    this.currentLevel = level;
    this.isDevelopmentMode = (level <= this.LEVELS.INFO);
  },
  
  // Log methods
  debug(...args) {
    if (this.currentLevel <= this.LEVELS.DEBUG) {
      console.log(...args);
    }
  },
  
  info(...args) {
    if (this.currentLevel <= this.LEVELS.INFO) {
      console.log(...args);
    }
  },
  
  warn(...args) {
    if (this.currentLevel <= this.LEVELS.WARN) {
      console.warn(...args);
    }
  },
  
  error(...args) {
    if (this.currentLevel <= this.LEVELS.ERROR) {
      console.error(...args);
    }
  },
  
  // Group methods for better organization
  group(...args) {
    if (this.currentLevel <= this.LEVELS.DEBUG) {
      console.group(...args);
    }
  },
  
  groupCollapsed(...args) {
    if (this.currentLevel <= this.LEVELS.DEBUG) {
      console.groupCollapsed(...args);
    }
  },
  
  groupEnd() {
    if (this.currentLevel <= this.LEVELS.DEBUG) {
      console.groupEnd();
    }
  }
};

// Initialize with production mode by default
MMS_LOGGER.enableProductionMode();

// =========================================================================
// GLOBAL DEBUG FUNCTIONS (call from browser console)
// =========================================================================
window.MMS_DEBUG = {
  // Enable verbose logging for development
  enableVerbose: () => {
    MMS_LOGGER.enableDevelopmentMode();
    console.log('🟢 MMS Personalizer: Verbose logging enabled');
  },
  
  // Disable verbose logging (production mode)
  disableVerbose: () => {
    MMS_LOGGER.enableProductionMode();
    console.log('🚀 MMS Personalizer: Production logging enabled (warnings/errors only)');
  },
  
  // Set custom log level
  setLevel: (level) => {
    MMS_LOGGER.setLogLevel(level);
    console.log(`🔧 MMS Personalizer: Log level set to ${level}`);
  },
  
  // Get current log level
  getLevel: () => MMS_LOGGER.currentLevel,
  
  // Show available log levels
  showLevels: () => {
    console.log('📊 Available log levels:');
    console.log('  DEBUG (0): All logs');
    console.log('  INFO  (1): Info, warnings, errors');
    console.log('  WARN  (2): Warnings and errors only (default)');
    console.log('  ERROR (3): Errors only');
  }
};

// =========================================================================
// DEVELOPMENT MODE: Uncomment the line below to enable verbose logging
// =========================================================================
// MMS_LOGGER.enableDevelopmentMode();

// =========================================================================
// QUICK DEBUG: You can also set specific log levels:
// =========================================================================
// MMS_LOGGER.setLogLevel(MMS_LOGGER.LEVELS.DEBUG);    // All logs (most verbose)
// MMS_LOGGER.setLogLevel(MMS_LOGGER.LEVELS.INFO);     // Info, warnings, errors
// MMS_LOGGER.setLogLevel(MMS_LOGGER.LEVELS.WARN);     // Warnings and errors only (default)
// MMS_LOGGER.setLogLevel(MMS_LOGGER.LEVELS.ERROR);    // Errors only (most restrictive)

/* =========================================================================
   𝙈𝙈𝙎  personaliser – Responsive Split/Tab Layout Version
   ========================================================================= */

class Store {
  constructor(reducer, initialState) {
    this.reducer = reducer;
    this.state = this.reducer(initialState, { type: '__INIT__' });
    this.listeners = [];
  }

  getState() { return this.state; }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  dispatch(actionOrThunk) {
    const isThunk = typeof actionOrThunk === 'function';
    const action = isThunk ? actionOrThunk(this.state) : actionOrThunk;

    // For logging, create a version of the state that truncates long image data
    const loggableState = (state) => {
        const tempState = JSON.parse(JSON.stringify(state)); // Deep copy to avoid mutation
        for (const key in tempState.magnets) {
            if (tempState.magnets[key].originalImage) {
                tempState.magnets[key].originalImage = tempState.magnets[key].originalImage.substring(0, 50) + '...';
            }
            if (tempState.magnets[key].personalizedImage) {
                tempState.magnets[key].personalizedImage = tempState.magnets[key].personalizedImage.substring(0, 50) + '...';
            }
        }
        return tempState;
    };

    MMS_LOGGER.groupCollapsed(`ACTION: ${isThunk ? 'THUNK' : action.type}`);
    MMS_LOGGER.debug('%c State Before:', 'color: #9E9E9E; font-weight: bold;', loggableState(this.state));
    if (isThunk) {
      MMS_LOGGER.debug('%c Thunk Payload:', 'color: #03A9F4; font-weight: bold;', '(function)');
    } else {
      MMS_LOGGER.debug('%c Action Payload:', 'color: #03A9F4; font-weight: bold;', action);
    }
    
    this.state = this.reducer(this.state, actionOrThunk);
    
    MMS_LOGGER.debug('%c State After:', 'color: #4CAF50; font-weight: bold;', loggableState(this.state));
    MMS_LOGGER.groupEnd();

    this.listeners.forEach(listener => listener(this.state));
  }
}

// --- 2. FabricWrapper Class ---
const ASPECT_RATIO_HORIZONTAL = 3.5 / 2.5;
const ASPECT_RATIO_VERTICAL = 2.5 / 3.5;

// Image quality configuration
const IMAGE_QUALITY_CONFIG = {
  PRODUCTION_SCALE: 4,        // 4x display size for high quality
  JPEG_QUALITY: 0.9,          // JPEG compression quality (0.1-1.0)
  MAX_FILE_SIZE_MB: 10        // Maximum file size for CloudFlare upload
};

class FabricWrapper {
  constructor(canvasEl, store) {
    this.canvasEl = canvasEl;
    this.store = store;
    this.fabricCanvas = null;
    this.currentImageId = null;
    this.imageObject = null;
    this.cropRect = null;
    this.isDragging = false;
    this.lastPosX = 0;
    this.lastPosY = 0;
    this.currentOrientation = null;
    this.workingImageUrl = null; // Holds the current working image (cropped or original)
    this.init();
  }

  // Utility to ensure there is only one entry per text ID
  dedupeTextObjects(list = []) {
    const map = new Map();
    list.forEach(obj => {
      // Always let the last occurrence win so the most recent
      // updates are preserved when duplicates exist
      map.set(obj.id, obj);
    });
    return Array.from(map.values());
  }

  init() {
    // Disable selection by default for smooth pan
    this.fabricCanvas = new fabric.Canvas(this.canvasEl, { backgroundColor: '#f1f5f9', selection: false });
    
    // Bind event handlers for text selection
    this.fabricCanvas.on('selection:created', (e) => {
      this.handleSelection(e);
    });
    
    this.fabricCanvas.on('selection:updated', (e) => {
      this.handleSelection(e);
    });
    
    this.fabricCanvas.on('selection:cleared', () => {
      this.handleSelectionClear();
    });
    
    this.fabricCanvas.on('object:modified', (e) => this.handleObjectModified(e));
    this.fabricCanvas.on('object:moving', (e) => this.handleObjectMoving(e));

    // --- Smooth, lens-like pan logic ---
    // Allow drag from anywhere in the frame
    this.isDragging = false;
    this.dragStartPointer = null;
    this.dragStartImagePos = null;
    // Mouse drag (anywhere in frame)
    this.fabricCanvas.on('mouse:down', (opt) => {
      const evt = opt.e;
      const pointer = this.fabricCanvas.getPointer(evt);
      const activeObj = this.fabricCanvas.getActiveObject();
      const isCropping = this.store.getState().ui.workspaceMode === 'cropping';
      if (!isCropping && (!activeObj || activeObj.type !== 'i-text') && this.imageObject) {
        if (!isFinite(pointer.x) || !isFinite(pointer.y)) {
          console.warn('[Pan Debug] mouse:down - pointer invalid', pointer);
          return;
        }
        this.isDragging = true;
        this.dragStartPointer = { x: pointer.x, y: pointer.y };
        // Guard: if left/top is NaN, reset to canvas center
        if (!isFinite(this.imageObject.left) || !isFinite(this.imageObject.top)) {
          const canvasWidth = this.fabricCanvas.getWidth();
          const canvasHeight = this.fabricCanvas.getHeight();
          this.imageObject.left = canvasWidth / 2;
          this.imageObject.top = canvasHeight / 2;
          console.warn('[Pan Debug] mouse:down - imageObject.left/top was NaN, reset to center', { left: this.imageObject.left, top: this.imageObject.top });
        }
        this.dragStartImagePos = { left: this.imageObject.left, top: this.imageObject.top };
            MMS_LOGGER.debug('[Pan Debug] mouse:down (set dragStartImagePos)', this.dragStartImagePos);
    MMS_LOGGER.debug('[Pan Debug] mouse:down dragStartPointer', this.dragStartPointer);
      }
    });
    this.fabricCanvas.on('mouse:move', (opt) => {
      if (!this.isDragging || !this.imageObject || !this.dragStartPointer || !this.dragStartImagePos) return;
      const evt = opt.e;
      const pointer = this.fabricCanvas.getPointer(evt);
      if (!isFinite(pointer.x) || !isFinite(pointer.y)) {
        console.warn('[Pan Debug] mouse:move - pointer invalid', pointer);
        return;
      }
      const deltaX = pointer.x - this.dragStartPointer.x;
      const deltaY = pointer.y - this.dragStartPointer.y;
      this.imageObject.left = this.dragStartImagePos.left + deltaX;
      this.imageObject.top = this.dragStartImagePos.top + deltaY;
      this.clampImagePosition();
      this.fabricCanvas.requestRenderAll();
              MMS_LOGGER.debug(`[Pan Debug] mouse:move curr=(${pointer.x},${pointer.y}) delta=(${deltaX},${deltaY}) image=(${this.imageObject.left},${this.imageObject.top})`);
    });
    this.fabricCanvas.on('mouse:up', () => {
      if (this.imageObject) {
        MMS_LOGGER.debug('[Pan Debug] mouse:up', {
          image: { left: this.imageObject.left, top: this.imageObject.top }
        });
      }
      this.isDragging = false;
      this.dragStartPointer = null;
      this.dragStartImagePos = null;
    });
    // Touch drag (anywhere in frame)
    const canvasEl = this.fabricCanvas.upperCanvasEl;
    canvasEl.addEventListener('touchstart', (e) => {
      const isCropping = this.store.getState().ui.workspaceMode === 'cropping';
      const activeObj = this.fabricCanvas.getActiveObject();
      if (e.touches.length === 1 && !isCropping && (!activeObj || activeObj.type !== 'i-text') && this.imageObject) {
        const rect = canvasEl.getBoundingClientRect();
        this.isDragging = true;
        this.dragStartPointer = {
          x: (e.touches[0].clientX - rect.left) * (this.fabricCanvas.getWidth() / rect.width),
          y: (e.touches[0].clientY - rect.top) * (this.fabricCanvas.getHeight() / rect.height)
        };
        // Guard: if left/top is NaN, reset to canvas center
        if (!isFinite(this.imageObject.left) || !isFinite(this.imageObject.top)) {
          const canvasWidth = this.fabricCanvas.getWidth();
          const canvasHeight = this.fabricCanvas.getHeight();
          this.imageObject.left = canvasWidth / 2;
          this.imageObject.top = canvasHeight / 2;
          console.warn('[Pan Debug] touchstart - imageObject.left/top was NaN, reset to center', { left: this.imageObject.left, top: this.imageObject.top });
        }
        this.dragStartImagePos = { left: this.imageObject.left, top: this.imageObject.top };
        MMS_LOGGER.debug('[Pan Debug] touchstart (set dragStartImagePos)', this.dragStartImagePos);
        MMS_LOGGER.debug('[Pan Debug] touchstart dragStartPointer', this.dragStartPointer);
      }
      e.preventDefault();
    }, { passive: false });
    canvasEl.addEventListener('touchmove', (e) => {
      if (e.touches.length === 1 && this.isDragging && this.imageObject && this.dragStartPointer && this.dragStartImagePos) {
        const rect = canvasEl.getBoundingClientRect();
        const currX = (e.touches[0].clientX - rect.left) * (this.fabricCanvas.getWidth() / rect.width);
        const currY = (e.touches[0].clientY - rect.top) * (this.fabricCanvas.getHeight() / rect.height);
        const deltaX = currX - this.dragStartPointer.x;
        const deltaY = currY - this.dragStartPointer.y;
        this.imageObject.left = this.dragStartImagePos.left + deltaX;
        this.imageObject.top = this.dragStartImagePos.top + deltaY;
        this.clampImagePosition();
        this.fabricCanvas.requestRenderAll();
        MMS_LOGGER.debug(`[Pan Debug] touchmove curr=(${currX},${currY}) delta=(${deltaX},${deltaY}) image=(${this.imageObject.left},${this.imageObject.top})`);
      }
      e.preventDefault();
    }, { passive: false });
    canvasEl.addEventListener('touchend', (e) => {
      if (this.imageObject) {
        MMS_LOGGER.debug('[Pan Debug] touchend', {
          image: { left: this.imageObject.left, top: this.imageObject.top }
        });
      }
      this.isDragging = false;
      this.dragStartPointer = null;
      this.dragStartImagePos = null;
      e.preventDefault();
    }, { passive: false });

    // Add keyboard delete support
    document.addEventListener('keydown', (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && this.fabricCanvas.getActiveObject()) {
        const activeObject = this.fabricCanvas.getActiveObject();
        if (
          activeObject &&
          activeObject.type === 'i-text' &&
          !activeObject.isEditing // Only delete if not editing
        ) {
          e.preventDefault();
          this.deleteSelectedText();
        }
      }
    });

    // Restore mouse wheel zoom support
    this.fabricCanvas.on('mouse:wheel', (opt) => {
      const evt = opt.e;
      const activeObj = this.fabricCanvas.getActiveObject();
      const isCropping = this.store.getState().ui.workspaceMode === 'cropping';
      if (!isCropping && (!activeObj || activeObj.type !== 'i-text') && this.imageObject) {
        // Zoom around mouse pointer
        const pointer = this.fabricCanvas.getPointer(evt);
        const oldScale = this.imageObject.scaleX;
        let zoom = oldScale * (0.999 ** evt.deltaY);
        // Clamp scale to minImageScale (fit-to-frame) and max 3
        const minScale = this.minImageScale || 1;
        if (zoom > 5) zoom = 5;
        if (zoom < minScale) zoom = minScale;
        if (zoom > 3) zoom = 3;
        // Calculate new position to keep pointer under mouse
        const dx = pointer.x - this.imageObject.left;
        const dy = pointer.y - this.imageObject.top;
        this.imageObject.left = pointer.x - dx * (zoom / oldScale);
        this.imageObject.top = pointer.y - dy * (zoom / oldScale);
        this.imageObject.scaleX = zoom;
        this.imageObject.scaleY = zoom;
        this.clampImagePosition();
        this.fabricCanvas.requestRenderAll();
        evt.preventDefault();
        evt.stopPropagation();
      }
    });

    // --- Pinch-to-zoom for mobile (two-finger gesture) ---
    // Guard state for pinch gesture
    this.isPinching = false;
    this.pinchStartDistance = 0;
    this.pinchStartScale = 1;
    this.pinchMidpoint = null;

    // Helper to get distance between two touches
    function getTouchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }
    // Helper to get midpoint between two touches (in canvas coordinates)
    function getTouchMidpoint(touches, rect, canvas) {
      const x = (touches[0].clientX + touches[1].clientX) / 2 - rect.left;
      const y = (touches[0].clientY + touches[1].clientY) / 2 - rect.top;
      return {
        x: x * (canvas.getWidth() / rect.width),
        y: y * (canvas.getHeight() / rect.height)
      };
    }

    canvasEl.addEventListener('touchstart', (e) => {
      const mode = this.store.getState().ui.workspaceMode;
      const activeObj = this.fabricCanvas.getActiveObject();
      // Only allow pinch/zoom in pan or select mode, not cropping or text/artwork
      const allowPinch = (mode === 'pan' || mode === 'select') && (!activeObj || activeObj.type !== 'i-text') && this.imageObject;
      if (e.touches.length === 2 && allowPinch) {
        this.isPinching = true;
        this.pinchStartDistance = getTouchDistance(e.touches);
        this.pinchStartScale = this.imageObject.scaleX;
        const rect = canvasEl.getBoundingClientRect();
        this.pinchMidpoint = getTouchMidpoint(e.touches, rect, this.fabricCanvas);
        // Optional: add a CSS class for feedback
        canvasEl.classList.add('pinching');
      }
      // Let existing pan logic handle single-finger drag
    }, { passive: false });

    canvasEl.addEventListener('touchmove', (e) => {
      if (this.isPinching && e.touches.length === 2) {
        e.preventDefault();
        const mode = this.store.getState().ui.workspaceMode;
        const activeObj = this.fabricCanvas.getActiveObject();
        const allowPinch = (mode === 'pan' || mode === 'select') && (!activeObj || activeObj.type !== 'i-text') && this.imageObject;
        if (!allowPinch) return;
        const newDistance = getTouchDistance(e.touches);
        let scale = this.pinchStartScale * (newDistance / this.pinchStartDistance);
        // Clamp scale
        const minScale = this.minImageScale || 1;
        if (scale < minScale) scale = minScale;
        if (scale > 3) scale = 3;
        // Optionally, zoom around the pinch midpoint
        if (this.pinchMidpoint) {
          const img = this.imageObject;
          const oldScale = img.scaleX;
          const pointer = this.pinchMidpoint;
          const dx = pointer.x - img.left;
          const dy = pointer.y - img.top;
          img.left = pointer.x - dx * (scale / oldScale);
          img.top = pointer.y - dy * (scale / oldScale);
        }
        this.imageObject.scaleX = scale;
        this.imageObject.scaleY = scale;
        this.clampImagePosition();
        this.fabricCanvas.requestRenderAll();
      }
    }, { passive: false });

    canvasEl.addEventListener('touchend', (e) => {
      if (this.isPinching && e.touches.length < 2) {
        this.isPinching = false;
        this.pinchStartDistance = 0;
        this.pinchStartScale = 1;
        this.pinchMidpoint = null;
        canvasEl.classList.remove('pinching');
      }
    }, { passive: false });
  }

  handleSelection(e) {
    // Handle both event structures: selection:created uses e.selected, object:selected uses e.target
    const activeObject = e.target || (e.selected && e.selected[0]);
    
    if (activeObject && activeObject.type === 'i-text') {
              MMS_LOGGER.debug('📝 Text object selected:', activeObject.id);
      
      // Update UI state for text selection
      this.store.dispatch(currentState => ({
        ...currentState,
        ui: {
          ...currentState.ui,
          isTextSelected: true,
          selectedTextId: activeObject.id,
          selectedTextProperties: {
            id: activeObject.id,
            text: activeObject.text,
            fontFamily: activeObject.fontFamily,
            fontSize: activeObject.fontSize,
            fill: activeObject.fill
          }
        }
      }));
    } else {
      // If not a text object, clear text selection state
      this.handleSelectionClear();
    }
  }

  handleSelectionClear() {
            MMS_LOGGER.debug('🔄 Selection cleared');
    this.store.dispatch(currentState => ({
      ...currentState,
      ui: {
        ...currentState.ui,
        isTextSelected: false,
        selectedTextId: null,
        selectedTextProperties: null
      }
    }));
  }

  handleObjectModified(e) {
    const obj = e.target;
    if (obj && obj.type === 'i-text') {
      this.syncTextObjectToState(obj);
    }
  }

  handleObjectMoving(e) {
    const obj = e.target;
    if (obj && obj.type === 'i-text') {
      // Debounced save during movement
      clearTimeout(this.moveTimeout);
      this.moveTimeout = setTimeout(() => {
        this.syncTextObjectToState(obj);
      }, 100);
    }
  }

  loadImage(imageUrl, magnetId) {
    if (!imageUrl) { this.clearCanvas(); return; }
    this.currentImageId = magnetId;
    this.fabricCanvas.clear();
    this.cropRect = null; // Clear old crop rect
    this.imageObject = null; // Clear old image object

    fabric.Image.fromURL(imageUrl, (img) => {
      this.imageObject = img;
      img.set({
        selectable: false,
        evented: true, // Allow pan events to fire on image
        left: 0,
        top: 0,
        opacity: 1 // Always reset opacity to 1 when loading a new image
      });
      this.fabricCanvas.add(img);
      this.fabricCanvas.sendToBack(img); // Ensure image stays behind text objects
      this.resetView(); // Set initial pan and zoom correctly
      // Default to pan mode after image placement
      this.store.dispatch(currentState => ({
        ...currentState,
        ui: { ...currentState.ui, workspaceMode: 'pan' }
      }));
    }, { crossOrigin: 'anonymous' });
  }

  clearCanvas() {
    this.fabricCanvas.clear();
    this.currentImageId = null;
    this.imageObject = null;
    this.cropRect = null;
    // Reset orientation so next render can reapply dimensions correctly
    this.currentOrientation = null;
    this.fabricCanvas.backgroundColor = '#f1f5f9';
    this.fabricCanvas.renderAll();
  }

  addText(text) {
    // Prevent render during text addition
    this._isAddingText = true;
    
    const textId = `text_${new Date().getTime()}`;
    const textObject = new fabric.IText(text, { 
      left: this.fabricCanvas.width / 2, 
      top: this.fabricCanvas.height / 2, 
      originX: 'center', 
      originY: 'center', 
      fontFamily: 'Arial', 
      fontSize: 40, 
      fill: '#000000', 
      cornerColor: '#0ea5e9', 
      cornerSize: 10, 
      transparentCorners: false,
      borderColor: '#0ea5e9',
      id: textId,
      selectable: true,
      evented: true
    });

    // Add to canvas and select immediately
    this.fabricCanvas.add(textObject);
    this.fabricCanvas.setActiveObject(textObject);
    this.fabricCanvas.renderAll();
    
    // Create text data for state
    const textData = {
      id: textId,
      text: text,
      left: textObject.left,
      top: textObject.top,
      fontFamily: textObject.fontFamily,
      fontSize: textObject.fontSize,
      fill: textObject.fill,
      originX: textObject.originX,
      originY: textObject.originY,
      angle: textObject.angle || 0,
      scaleX: textObject.scaleX || 1,
      scaleY: textObject.scaleY || 1,
      skewX: textObject.skewX || 0,
      skewY: textObject.skewY || 0
    };
    
    // Update state
    this.store.dispatch(currentState => {
      const { activeMagnetId } = currentState.ui;
      if (!activeMagnetId) return currentState;

      const newMagnets = { ...currentState.magnets };
      const currentMagnet = newMagnets[activeMagnetId];
      
      // Add new text object to state
      const textObjects = currentMagnet.textObjects || [];
      textObjects.push(textData);
      
      newMagnets[activeMagnetId] = {
        ...currentMagnet,
        textObjects: this.dedupeTextObjects(textObjects)
      };

      return {
        ...currentState,
        magnets: newMagnets,
        ui: {
          ...currentState.ui,
          isTextSelected: true,
          selectedTextId: textId,
          selectedTextProperties: {
            text: text,
            fontFamily: textObject.fontFamily,
            fontSize: textObject.fontSize,
            fill: textObject.fill
          }
        }
      };
    });

    // Clear the adding flag
    this._isAddingText = false;
  }

  updateTextProperty(property, value) {
    const activeObject = this.fabricCanvas.getActiveObject();
    if (activeObject && activeObject.type === 'i-text') {
      if (property === 'text') {
        activeObject.text = value;
      } else {
        activeObject.set(property, value);
      }
      this.fabricCanvas.renderAll();
      
      // Also update the state immediately
      this.syncTextObjectToState(activeObject);
    }
  }

  syncTextObjectToState(textObject) {
    if (!textObject || !textObject.id) return;
    
    const textData = {
      id: textObject.id,
      text: textObject.text,
      left: textObject.left,
      top: textObject.top,
      fontFamily: textObject.fontFamily,
      fontSize: textObject.fontSize,
      fill: textObject.fill,
      originX: textObject.originX,
      originY: textObject.originY,
      // Preserve additional transformation properties
      angle: textObject.angle || 0,
      scaleX: textObject.scaleX || 1,
      scaleY: textObject.scaleY || 1,
      skewX: textObject.skewX || 0,
      skewY: textObject.skewY || 0
    };
    
    this.store.dispatch(currentState => {
      const { activeMagnetId } = currentState.ui;
      if (!activeMagnetId) return currentState;

      const newMagnets = { ...currentState.magnets };
      const currentMagnet = newMagnets[activeMagnetId];

      // Remove any existing entry for this text object then add the updated data
      const filtered = (currentMagnet.textObjects || []).filter(obj => obj.id !== textObject.id);
      filtered.push(textData);

      newMagnets[activeMagnetId] = {
        ...currentMagnet,
        textObjects: this.dedupeTextObjects(filtered)
      };
      return { ...currentState, magnets: newMagnets };
    });
  }

  render(magnetState) {
    if (!magnetState) return;

    // Skip render if we're in the middle of adding text
    if (this._isAddingText) {
              MMS_LOGGER.debug('⚠️ Skipping render during text addition');
      return;
    }

    // Throttle renders to prevent infinite loops
    if (this._renderInProgress) {
              MMS_LOGGER.debug('⚠️ Render already in progress, skipping...');
      return;
    }
    
    this._renderInProgress = true;

    try {
      // Store the currently selected object before making changes
      const currentlySelected = this.fabricCanvas.getActiveObject();
      const selectedObjectId = currentlySelected?.id;

      // Update canvas dimensions based on orientation (skip if already processing)
      if (!this._orientationChangeInProgress) {
        this.updateCanvasOrientation(magnetState.orientation);
      }

      // --- Sync Text Objects from State to Canvas ---
      if (magnetState.textObjects && magnetState.textObjects.length > 0) {
        MMS_LOGGER.debug('Syncing text objects with state:', magnetState.textObjects);
        
        // Get current canvas objects
        const canvasObjects = this.fabricCanvas.getObjects('i-text');
        const canvasObjectIds = new Set(canvasObjects.map(obj => obj.id));
        const stateObjectIds = new Set(magnetState.textObjects.map(obj => obj.id));
        
        // Remove objects that are no longer in state
        canvasObjects.forEach(obj => {
          if (!stateObjectIds.has(obj.id)) {
            MMS_LOGGER.debug('Removing deleted object:', obj.id);
            this.fabricCanvas.remove(obj);
          }
        });
        
        // Add or update objects from state
        magnetState.textObjects.forEach(textState => {
          let canvasObj = canvasObjects.find(o => o.id === textState.id);
          
          if (!canvasObj) {
            // Object doesn't exist on canvas, create it
            MMS_LOGGER.debug('Creating new text object:', textState.id);
            canvasObj = new fabric.IText(textState.text, {
              left: textState.left,
              top: textState.top,
              fontFamily: textState.fontFamily,
              fontSize: textState.fontSize,
              fill: textState.fill,
              originX: textState.originX || 'center',
              originY: textState.originY || 'center',
              id: textState.id,
              angle: textState.angle || 0,
              scaleX: textState.scaleX || 1,
              scaleY: textState.scaleY || 1,
              skewX: textState.skewX || 0,
              skewY: textState.skewY || 0,
              cornerColor: '#0ea5e9',
              cornerSize: 10,
              transparentCorners: false,
              borderColor: '#0ea5e9',
              selectable: true,
              evented: true
            });
            this.fabricCanvas.add(canvasObj);
          } else {
            // Update existing object properties
            canvasObj.set({
              text: textState.text,
              left: textState.left,
              top: textState.top,
              fontFamily: textState.fontFamily,
              fontSize: textState.fontSize,
              fill: textState.fill,
              angle: textState.angle || 0,
              scaleX: textState.scaleX || 1,
              scaleY: textState.scaleY || 1,
              skewX: textState.skewX || 0,
              skewY: textState.skewY || 0
            });
          }
        });
      } else {
        // No text objects in state, clear all from canvas
        const textObjects = this.fabricCanvas.getObjects('i-text');
        textObjects.forEach(obj => this.fabricCanvas.remove(obj));
      }

      // Ensure proper interaction mode is applied
      const currentMode = this.store.getState().ui.workspaceMode;
      this.setInteractionMode(currentMode);

      // Restore selection if needed
      if (selectedObjectId) {
        const objectToSelect = this.fabricCanvas.getObjects().find(obj => obj.id === selectedObjectId);
        if (objectToSelect) {
          this.fabricCanvas.setActiveObject(objectToSelect);
        }
      }

      this.fabricCanvas.renderAll();
    } catch (error) {
      console.error('❌ Error during render:', error);
    } finally {
      this._renderInProgress = false;
    }
  }

  enterCropMode() {
    if (this.cropRect || !this.imageObject) return;
    this.imageObject.set({ opacity: 0.5 });
    // Determine aspect ratio and crop box size based on orientation
    const isPortrait = this.currentOrientation === 'portrait';
    const aspectRatio = isPortrait ? ASPECT_RATIO_VERTICAL : ASPECT_RATIO_HORIZONTAL;

    // Use the current viewport (visible area) for crop window calculation
    const vpt = this.fabricCanvas.viewportTransform;
    const canvasWidth = this.fabricCanvas.getWidth();
    const canvasHeight = this.fabricCanvas.getHeight();
    // Visible area in canvas coordinates
    const visibleLeft = -vpt[4] / vpt[0];
    const visibleTop = -vpt[5] / vpt[3];
    const visibleWidth = canvasWidth / vpt[0];
    const visibleHeight = canvasHeight / vpt[3];

    // Find the largest crop rect that fits within the visible area, with the correct aspect ratio
    let cropWidth = visibleWidth * 0.8;
    let cropHeight = cropWidth / aspectRatio;
    if (cropHeight > visibleHeight * 0.8) {
      cropHeight = visibleHeight * 0.8;
      cropWidth = cropHeight * aspectRatio;
    }
    // Center the crop rect within the visible area
    const cropLeft = visibleLeft + visibleWidth / 2;
    const cropTop = visibleTop + visibleHeight / 2;

    // Debug logs
            MMS_LOGGER.debug('[Crop Debug] Canvas size:', canvasWidth, canvasHeight);
        MMS_LOGGER.debug('[Crop Debug] Viewport transform:', vpt);
        MMS_LOGGER.debug('[Crop Debug] Visible area in canvas coords:', visibleLeft, visibleTop, visibleWidth, visibleHeight);
        MMS_LOGGER.debug('[Crop Debug] Crop window size:', cropWidth, cropHeight);

    this.cropRect = new fabric.Rect({
      left: cropLeft,
      top: cropTop,
      originX: 'center',
      originY: 'center',
      width: cropWidth,
      height: cropHeight,
      fill: 'rgba(0,0,0,0.3)',
      stroke: 'rgba(255, 255, 255, 0.7)',
      strokeWidth: 2,
      strokeDashArray: [5, 5],
      lockRotation: true,
      selectable: true,
      evented: true
    });
    this.fabricCanvas.add(this.cropRect);
    this.fabricCanvas.bringToFront(this.cropRect);
    this.fabricCanvas.setActiveObject(this.cropRect);
    this.fabricCanvas.renderAll();
  }

  exitCropMode() {
    if (!this.cropRect) return;
    if (this.imageObject) { this.imageObject.set({ opacity: 1 }); }
    this.fabricCanvas.remove(this.cropRect);
    this.cropRect = null;
    this.fabricCanvas.renderAll();
  }

  applyCrop() {
    if (!this.cropRect || !this.imageObject) { 
              MMS_LOGGER.debug('[Crop Debug] No cropRect or imageObject present');
      return null; 
    }
    // Calculate crop rectangle in image pixel space
    const cropZone = this.cropRect.getBoundingRect();
    const img = this.imageObject;
    const canvas = this.fabricCanvas;
    // Get the current viewport transform
    const vpt = canvas.viewportTransform;
    // Invert the viewport transform to map screen -> canvas coordinates
    function invertTransform(vpt) {
      // vpt: [a, b, c, d, e, f] (2D affine)
      const [a, b, c, d, e, f] = vpt;
      const det = a * d - b * c;
      return [
        d / det,
        -b / det,
        -c / det,
        a / det,
        (c * f - d * e) / det,
        (b * e - a * f) / det
      ];
    }
    function transformPoint(x, y, t) {
      return [
        t[0] * x + t[2] * y + t[4],
        t[1] * x + t[3] * y + t[5]
      ];
    }
    const invVpt = invertTransform(vpt);
    // Transform crop rect corners from screen to canvas coordinates
    const cropLeftTop = transformPoint(cropZone.left, cropZone.top, invVpt);
    const cropRightBottom = transformPoint(cropZone.left + cropZone.width, cropZone.top + cropZone.height, invVpt);
    const rect = {
      left: cropLeftTop[0],
      top: cropLeftTop[1],
      width: cropRightBottom[0] - cropLeftTop[0],
      height: cropRightBottom[1] - cropLeftTop[1]
    };
    // Get the image's position and scale on the canvas
    const imgLeft = img.left - (img.width * img.scaleX) / 2;
    const imgTop = img.top - (img.height * img.scaleY) / 2;
    // Convert crop rect to image pixel space
    const cropX = Math.max(0, Math.round((rect.left - imgLeft) / img.scaleX));
    const cropY = Math.max(0, Math.round((rect.top - imgTop) / img.scaleY));
    const cropW = Math.round(rect.width / img.scaleX);
    const cropH = Math.round(rect.height / img.scaleY);
    // Debug logs
            MMS_LOGGER.debug('[Crop Debug] Viewport transform:', vpt);
        MMS_LOGGER.debug('[Crop Debug] Inverse VPT:', invVpt);
        MMS_LOGGER.debug('[Crop Debug] Crop rect (screen):', cropZone);
        MMS_LOGGER.debug('[Crop Debug] Crop rect (canvas):', rect);
        MMS_LOGGER.debug('[Crop Debug] Crop rect (image px):', cropX, cropY, cropW, cropH);
    // Remove cropRect and restore image opacity before cropping
    img.set({ opacity: 1 });
    this.fabricCanvas.remove(this.cropRect);
    this.cropRect = null;
    this.fabricCanvas.renderAll();
    // Create an offscreen canvas to crop the image
    const offCanvas = document.createElement('canvas');
    offCanvas.width = cropW;
    offCanvas.height = cropH;
    const ctx = offCanvas.getContext('2d');
    // Draw the original image source, cropping to the calculated area
    const imageElement = img._element || img._originalElement || img.getElement();
    ctx.drawImage(imageElement, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
    const croppedDataUrl = offCanvas.toDataURL('image/jpeg', IMAGE_QUALITY_CONFIG.JPEG_QUALITY);
            MMS_LOGGER.debug('[Crop Debug] Cropped data URL length:', croppedDataUrl.length);
    return croppedDataUrl;
  }

  getFinalImageData() {
    const productionScale = IMAGE_QUALITY_CONFIG.PRODUCTION_SCALE;

    return this.fabricCanvas.toDataURL({
      format: 'jpeg',
      quality: IMAGE_QUALITY_CONFIG.JPEG_QUALITY,
      multiplier: productionScale
    });
  }

  updateCanvasOrientation(orientation) {
    // Instead of hard-coding dimensions, get them from the CSS surface
    const frame = document.querySelector('.mms-workspace-canvas-container');
    if (!frame) {
              MMS_LOGGER.debug(`❌ [Canvas Orientation] No frame element found`);
      return;
    }

    // Skip if orientation is already applied
    if (this.currentOrientation === orientation) {
              MMS_LOGGER.debug(`⚠️ [Canvas Orientation] Already in ${orientation} mode, skipping`);
      return;
    }
    
    // Prevent multiple orientation changes at once
    if (this._orientationChangeInProgress) {
              MMS_LOGGER.debug(`⚠️ [Canvas Orientation] Change already in progress, skipping...`);
      return;
    }
    this._orientationChangeInProgress = true;
    
            MMS_LOGGER.debug(`🔄 [Canvas Orientation] Starting orientation change to ${orientation}`);
    
    // Wait for CSS transition, then resize canvas to match frame
    setTimeout(() => {
      // Always use the actual frame size for canvas
      const rect = frame.getBoundingClientRect();
      const newWidth = rect.width;
      const newHeight = rect.height;
      
              MMS_LOGGER.debug(`📐 [Frame Dimensions] Using frame size for ${orientation}: ${newWidth}x${newHeight}`);
      
      const currentWidth = this.fabricCanvas.width;
      const currentHeight = this.fabricCanvas.height;
      
              MMS_LOGGER.debug(`🔍 [Canvas Orientation] Current: ${currentWidth}x${currentHeight}, New: ${newWidth}x${newHeight}`);
      
      // Only update if dimensions actually changed
      if (currentWidth !== newWidth || currentHeight !== newHeight) {
        MMS_LOGGER.debug(`📐 [Canvas Orientation] Resizing from ${currentWidth}x${currentHeight} to ${newWidth}x${newHeight} based on frame`);

        // Store current canvas state and include each object's id so
        // text objects can be recreated with the same identity after resize
        const canvasState = this.fabricCanvas.toJSON(['id']);
        
        // Resize the canvas
        this.fabricCanvas.setDimensions({
          width: newWidth,
          height: newHeight
        });
        
        // Clear and reload the image to properly fit new orientation
        this.refitImageForNewDimensions(newWidth, newHeight, canvasState);

        this.currentOrientation = orientation;
      } else {
        MMS_LOGGER.debug(`⚠️ [Canvas Orientation] No dimension change detected, skipping refit`);
        this.currentOrientation = orientation;
      }

      // Clear the orientation change flag
      this._orientationChangeInProgress = false;
    }, 300); // Wait for CSS transition to complete
  }

  refitImageForNewDimensions(newWidth, newHeight, canvasState) {
    // Get current magnet state to reload image
    const currentState = this.store.getState();
    const activeMagnetId = currentState.ui.activeMagnetId;
    if (!activeMagnetId) return;
    
    const magnetState = currentState.magnets[activeMagnetId];
    // Always use the original image for orientation changes to prevent compounding scaling.
    // Only use personalizedImage if a crop was just applied (i.e., canvasState is from a crop action).
    let imageUrl;
    if (canvasState && canvasState.cameFromCrop) {
      imageUrl = magnetState.personalizedImage || magnetState.originalImage;
    } else {
      imageUrl = magnetState.originalImage;
    }
    if (!imageUrl) return;
    
            MMS_LOGGER.debug(`🔄 Refitting image for new orientation: ${newWidth}x${newHeight}`);
    
    // Clear canvas
    this.fabricCanvas.clear();
    this.imageObject = null;
    
    // Reload image with proper scaling for new dimensions
    fabric.Image.fromURL(imageUrl, (img) => {
      this.imageObject = img;
      // Always use the original image dimensions
      const imageAspect = img.width / img.height;
      const frameAspect = newWidth / newHeight;
      let scale;
      if (imageAspect > frameAspect) {
        // Image is wider, scale to fill height
        scale = newHeight / img.height;
      } else {
        // Image is taller, scale to fill width
        scale = newWidth / img.width;
      }
      img.set({
        scaleX: scale,
        scaleY: scale,
        left: newWidth / 2,
        top: newHeight / 2,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false
      });
      this.fabricCanvas.add(img);
      this.fabricCanvas.sendToBack(img);
      // Restore text objects if any existed
      if (canvasState && canvasState.objects) {
        const textObjects = canvasState.objects.filter(obj => obj.type === 'i-text');
        const restoredData = [];
        textObjects.forEach(textObj => {
          // Preserve the original id when recreating text objects so subsequent
          // edits update the same item in state
          const { text: txt, id, ...opts } = textObj;
          const text = new fabric.IText(txt, { id, ...opts });
          this.fabricCanvas.add(text);
          restoredData.push({
            id,
            text: txt,
            left: text.left,
            top: text.top,
            fontFamily: text.fontFamily,
            fontSize: text.fontSize,
            fill: text.fill,
            originX: text.originX,
            originY: text.originY,
            angle: text.angle || 0,
            scaleX: text.scaleX || 1,
            scaleY: text.scaleY || 1,
            skewX: text.skewX || 0,
            skewY: text.skewY || 0
          });
        });

        // Sync restored text objects back to state to prevent duplicates
        this.store.dispatch(currentState => {
          const { activeMagnetId } = currentState.ui;
          if (!activeMagnetId) return currentState;

          const newMagnets = { ...currentState.magnets };
          const currentMagnet = newMagnets[activeMagnetId];
          newMagnets[activeMagnetId] = {
            ...currentMagnet,
            textObjects: this.dedupeTextObjects(restoredData)
          };
          return { ...currentState, magnets: newMagnets };
        });
      }
      this.fabricCanvas.renderAll();
      // Reapply interaction mode so restored objects remain selectable
      const currentMode = this.store.getState().ui.workspaceMode;
      this.setInteractionMode(currentMode);
              MMS_LOGGER.debug(`✅ Image refitted for new orientation with scale: ${scale.toFixed(3)}`);
    }, { crossOrigin: 'anonymous' });
  }

  setInteractionMode(mode) {
    // Enable selection only when in select mode (or text/artwork)
    const enableSelection = (mode === 'select' || mode === 'text' || mode === 'artwork');
    this.fabricCanvas.selection = enableSelection;
    this.fabricCanvas.forEachObject(obj => {
      if (obj === this.cropRect && mode === 'cropping') {
        obj.selectable = true;
        obj.evented = true;
      } else if (obj.type === 'i-text' || obj.isArtwork) {
        obj.selectable = enableSelection;
        obj.evented = enableSelection;
      } else {
        obj.selectable = false;
        obj.evented = false;
      }
    });
    // The main background image should never be selectable
    if(this.imageObject) {
      this.imageObject.selectable = false;
      this.imageObject.evented = false;
    }
  }

  resetView() {
    if (!this.imageObject) return;
    const canvas = this.fabricCanvas;
    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();

    const imgWidth = this.imageObject.width;
    const imgHeight = this.imageObject.height;
    
    // Calculate scale to cover canvas (ensuring no white space)
    const scaleX = canvasWidth / imgWidth;
    const scaleY = canvasHeight / imgHeight;
    const scale = Math.max(scaleX, scaleY); // Use larger scale to cover completely
    
    // Store the minimum allowed scale for this image/frame
    this.minImageScale = scale;
    
    // Reset canvas viewport to default
    canvas.setZoom(1);
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    
    // Set canvas background to transparent so we can see any gaps
    canvas.backgroundColor = 'transparent';
    
    // Scale and center the image object using center origin for easier positioning
    this.imageObject.set({
      scaleX: scale,
      scaleY: scale,
      left: canvasWidth / 2,
      top: canvasHeight / 2,
      originX: 'center',
      originY: 'center'
    });

            MMS_LOGGER.debug(`🔍 [Reset View Debug - Center Origin]`, {
      canvasSize: `${canvasWidth}x${canvasHeight}`,
      imageSize: `${imgWidth}x${imgHeight}`, 
      scale: scale.toFixed(3),
      scaledImageSize: `${(imgWidth * scale).toFixed(0)}x${(imgHeight * scale).toFixed(0)}`,
      imagePosition: `center: ${this.imageObject.left.toFixed(1)}, ${this.imageObject.top.toFixed(1)}`,
      shouldCover: `width: ${imgWidth * scale >= canvasWidth}, height: ${imgHeight * scale >= canvasHeight}`
    });
    
    canvas.requestRenderAll();
  }

  deleteSelectedText() {
    const activeObject = this.fabricCanvas.getActiveObject();
    if (!activeObject || activeObject.type !== 'i-text') return;

    // Get the text object's ID before removing it
    const textId = activeObject.id;

    // Remove from canvas
    this.fabricCanvas.remove(activeObject);
    this.fabricCanvas.discardActiveObject();
    this.fabricCanvas.renderAll();

    // Update state to remove the text object
    this.store.dispatch(currentState => {
      const { activeMagnetId } = currentState.ui;
      if (!activeMagnetId) return currentState;

      const newMagnets = { ...currentState.magnets };
      const currentMagnet = newMagnets[activeMagnetId];
      
      // Filter out the deleted text object
      const newTextObjects = (currentMagnet.textObjects || []).filter(obj => obj.id !== textId);
      
      newMagnets[activeMagnetId] = { ...currentMagnet, textObjects: newTextObjects };
      
      // Clear text selection in UI state
      return {
        ...currentState,
        magnets: newMagnets,
        ui: {
          ...currentState.ui,
          isTextSelected: false,
          selectedTextId: null,
          selectedTextProperties: null
        }
      };
    });

            MMS_LOGGER.debug('🗑️ Text object deleted:', textId);
  }

  // --- Add this method to constrain pan/zoom ---
  constrainViewportTransform() {
    if (!this.imageObject) return;
    const canvas = this.fabricCanvas;
    const vpt = canvas.viewportTransform;
    const zoom = canvas.getZoom();
    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();
    const img = this.imageObject;
    const imgWidth = img.width * img.scaleX;
    const imgHeight = img.height * img.scaleY;
    // Image position in canvas coordinates (centered)
    const imgLeft = img.left - imgWidth / 2;
    const imgTop = img.top - imgHeight / 2;
    // Image bounds in viewport coordinates
    const viewImgLeft = imgLeft * zoom + vpt[4];
    const viewImgTop = imgTop * zoom + vpt[5];
    const viewImgRight = (imgLeft + imgWidth) * zoom + vpt[4];
    const viewImgBottom = (imgTop + imgHeight) * zoom + vpt[5];
    // If any calculation is NaN, reset view
    if (!isFinite(viewImgLeft) || !isFinite(viewImgTop) || !isFinite(viewImgRight) || !isFinite(viewImgBottom)) {
      console.warn('[Pan Constraint] NaN detected in image bounds, calling resetView()');
      this.resetView();
      return;
    }
    // If the image is fully outside the visible frame, do not apply the pan (leave viewport unchanged)
    if (viewImgRight < 0 || viewImgLeft > canvasWidth || viewImgBottom < 0 || viewImgTop > canvasHeight) {
      console.warn('[Pan Constraint] Image would be fully out of frame, skipping pan');
      // Optionally, could call resetView() here as a fallback:
      // this.resetView();
      return;
    }
    // Calculate allowed pan range so image always covers the canvas
    let dx = 0, dy = 0;
    if (viewImgLeft > 0) dx = -viewImgLeft;
    if (viewImgTop > 0) dy = -viewImgTop;
    if (viewImgRight < canvasWidth) dx = canvasWidth - viewImgRight;
    if (viewImgBottom < canvasHeight) dy = canvasHeight - viewImgBottom;
    vpt[4] += dx;
    vpt[5] += dy;
    canvas.setViewportTransform(vpt);
  }

  // Clamp image position so it always covers the canvas (no whitespace)
  clampImagePosition() {
    if (!this.imageObject) return;
    const canvasWidth = this.fabricCanvas.getWidth();
    const canvasHeight = this.fabricCanvas.getHeight();
    const img = this.imageObject;
    const imgWidth = img.width * img.scaleX;
    const imgHeight = img.height * img.scaleY;
    const minScale = this.minImageScale || 1;
    const epsilon = 2;
    let bboxWidth = imgWidth;
    let bboxHeight = imgHeight;
    if (img.angle && img.angle % 360 !== 0) {
      const rad = (img.angle * Math.PI) / 180;
      bboxWidth = Math.abs(imgWidth * Math.cos(rad)) + Math.abs(imgHeight * Math.sin(rad));
      bboxHeight = Math.abs(imgWidth * Math.sin(rad)) + Math.abs(imgHeight * Math.cos(rad));
    }
    // If at minimum scale (fit-to-frame), center and lock position
    if (Math.abs(img.scaleX - minScale) < 0.0001 && Math.abs(img.scaleY - minScale) < 0.0001) {
      img.left = canvasWidth / 2;
      img.top = canvasHeight / 2;
      return;
    }
    // If image is smaller than canvas, center and disable pan
    if (bboxWidth <= canvasWidth && bboxHeight <= canvasHeight) {
      img.left = canvasWidth / 2;
      img.top = canvasHeight / 2;
      return;
    }
    if (bboxWidth <= canvasWidth) {
      img.left = canvasWidth / 2;
    } else {
      const minLeft = canvasWidth - bboxWidth / 2 + epsilon;
      const maxLeft = bboxWidth / 2 - epsilon;
      img.left = Math.max(minLeft, Math.min(maxLeft, img.left));
    }
    if (bboxHeight <= canvasHeight) {
      img.top = canvasHeight / 2;
    } else {
      const minTop = canvasHeight - bboxHeight / 2 + epsilon;
      const maxTop = bboxHeight / 2 - epsilon;
      img.top = Math.max(minTop, Math.min(maxTop, img.top));
    }
  }
}

// --- 3. ToolPanel Class ---
class ToolPanel {
  constructor(element, store, workspace) {
    this.element = element;
    this.store = store;
    this.workspace = workspace;
    this.init();
  }

  init() {
    // Use event delegation on the main editing workspace (parent of both toolbar and context panel)
    this.element.addEventListener('click', (event) => this.handlePanelClick(event));

    // Get references to the new UI elements
    this.floatingToolbar = this.element.querySelector('.mms-floating-toolbar');
    this.contextPanel = this.element.querySelector('.mms-context-panel');
    this.cropModeBtn = this.element.querySelector('[data-action="toggle-crop-mode"]');
    this.applyCropBtn = this.element.querySelector('[data-action="apply-crop"]');
    this.landscapeBtn = this.element.querySelector('.mms-landscape-btn');
    this.portraitBtn = this.element.querySelector('.mms-portrait-btn');
    
    // Text controls are handled separately with both 'change' and 'input' listeners
    this.textControls = this.element.querySelector('.mms-text-controls');
    this.cropControls = this.element.querySelector('.mms-crop-controls');
    this.stringColorControls = this.element.querySelector('#mms-string-color-controls');
    
    // Check if this is an ornament product
    const personalizerEl = document.querySelector('#mms-personaliser, .mms-personaliser-wrapper');
    this.isOrnament = personalizerEl && personalizerEl.dataset.productType === 'ornament';
    
    if (this.textControls) { 
      this.textControls.addEventListener('change', (event) => this.handleTextPropertyChange(event));
      this.textControls.addEventListener('input', (event) => this.handleTextPropertyChange(event));
    }
    
    this.fontFamilySelect = this.element.querySelector('select[name="font-family"]');
    this.fontSizeInput = this.element.querySelector('input[name="font-size"]');
    this.fontSizeValue = this.element.querySelector('#mms-font-size-value');
    this.fontColorInput = this.element.querySelector('input[name="font-color"]');
    this.textInput = this.element.querySelector('input[data-property="text"]');

    // Add click handler for delete text button
    const deleteTextBtn = this.element.querySelector('[data-action="delete-text"]');
    if (deleteTextBtn) {
      deleteTextBtn.addEventListener('click', () => {
        this.workspace.fabricWrapper.deleteSelectedText();
      });
    }

    // Font dropdown elements
    this.fontDropdown = this.element.querySelector('#mms-font-dropdown');
    this.fontDropdownTrigger = this.element.querySelector('#mms-font-dropdown-trigger');
    this.fontDropdownLabel = this.element.querySelector('#mms-font-dropdown-label');
    this.fontDropdownList = this.element.querySelector('#mms-font-dropdown-list');
    // Remove the in-panel <ul> from the DOM (will use portal)
    if (this.fontDropdownList) {
      this.fontDropdownList.parentNode.removeChild(this.fontDropdownList);
      this.fontDropdownList = null;
    }
    // Font options for portal
    this.fontOptions = [
      { label: 'Arial', family: 'Arial, Helvetica, sans-serif' },
      { label: 'Helvetica', family: 'Helvetica, Arial, sans-serif' },
      { label: 'Montserrat', family: "'Montserrat', Arial, sans-serif" },
      { label: 'Lato', family: "'Lato', Arial, sans-serif" },
      { label: 'Open Sans', family: "'Open Sans', Arial, sans-serif" },
      { label: 'Georgia', family: 'Georgia, serif' },
      { label: 'Merriweather', family: "'Merriweather', Georgia, serif" },
      { label: 'Times New Roman', family: "'Times New Roman', Times, serif" },
      { label: 'Pacifico', family: "'Pacifico', cursive" },
      { label: 'Dancing Script', family: "'Dancing Script', cursive" },
      { label: 'Courier New', family: "'Courier New', Courier, monospace" }
    ];
    // Portal dropdown logic
    if (this.fontDropdownTrigger) {
      this.fontDropdownTrigger.addEventListener('click', (e) => {
        e.stopPropagation();
        if (document.getElementById('mms-font-dropdown-portal')) {
          this.closeFontDropdownPortal();
        } else {
          this.openFontDropdownPortal();
        }
      });
    }
    document.addEventListener('click', (e) => {
      if (document.getElementById('mms-font-dropdown-portal')) {
        this.closeFontDropdownPortal();
      }
    });

    // Add event listener for the new Apply button in the top bar
    const applyBtn = this.element.querySelector('.mms-apply-finish-btn[data-action="apply-finish"]');
    if (applyBtn) {
      applyBtn.addEventListener('click', () => {
        // Existing logic for apply & finish
        this.handleApplyFinish();
      });
    }
  }

  openFontDropdownPortal() {
    // Remove any existing portal
    this.closeFontDropdownPortal();
    // Create portal element
    const portal = document.createElement('ul');
    portal.id = 'mms-font-dropdown-portal';
    portal.className = 'mms-font-dropdown-list';
    portal.setAttribute('role', 'listbox');
    portal.style.position = 'absolute';
    portal.style.zIndex = '2000';
    portal.style.display = 'block';
    // Set width to match trigger
    const triggerWidth = this.fontDropdown.offsetWidth;
    portal.style.minWidth = triggerWidth + 'px';
    portal.style.maxWidth = triggerWidth + 'px';
    // Position below trigger
    const rect = this.fontDropdownTrigger.getBoundingClientRect();
    portal.style.left = rect.left + window.scrollX + 'px';
    portal.style.top = rect.bottom + window.scrollY + 'px';
    // Add font options
    this.fontOptions.forEach(opt => {
      const li = document.createElement('li');
      li.setAttribute('data-font-family', opt.family);
      li.setAttribute('role', 'option');
      li.style.fontFamily = opt.family;
      li.textContent = opt.label;
      li.tabIndex = 0;
      li.addEventListener('click', (e) => {
        e.stopPropagation();
        this.handleFontDropdownChange(opt.family, opt.label);
        this.closeFontDropdownPortal();
      });
      li.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleFontDropdownChange(opt.family, opt.label);
          this.closeFontDropdownPortal();
        }
      });
      portal.appendChild(li);
    });
    document.body.appendChild(portal);
    // Focus first option for accessibility
    setTimeout(() => {
      const first = portal.querySelector('li');
      if (first) first.focus();
    }, 0);
  }

  closeFontDropdownPortal() {
    const portal = document.getElementById('mms-font-dropdown-portal');
    if (portal) portal.parentNode.removeChild(portal);
  }
  
  handlePanelClick(event) {
    const button = event.target.closest('button[data-action]');
    if (!button) return;

    const { action } = button.dataset;

    switch (action) {
      case 'set-orientation':
        this.handleSetOrientation(button.dataset.orientation);
        break;
      case 'toggle-crop-mode':
        this.handleCropModeToggle();
        break;
      case 'cancel-crop':
        this.handleCropCancel();
        break;
      case 'apply-crop':
        this.workspace.handleApplyCrop();
        break;
      case 'add-text':
        this.handleAddText();
        // Switch to select mode after adding text
        this.store.dispatch(currentState => ({
          ...currentState,
          ui: { ...currentState.ui, workspaceMode: 'select' }
        }));
        break;
      case 'reset-view':
        this.workspace.fabricWrapper.resetView();
        break;
      case 'toggle-pan-mode':
        this.store.dispatch(currentState => ({
          ...currentState,
          ui: { ...currentState.ui, workspaceMode: 'pan' }
        }));
        break;
      case 'toggle-select-mode':
        this.store.dispatch(currentState => ({
          ...currentState,
          ui: { ...currentState.ui, workspaceMode: 'select' }
        }));
        break;
    }
  }

  handleSetOrientation(orientation) {
    this.store.dispatch(currentState => {
      const { activeMagnetId } = currentState.ui;
      if (!activeMagnetId) return currentState;
      
      const newMagnets = { ...currentState.magnets };
      newMagnets[activeMagnetId] = { ...newMagnets[activeMagnetId], orientation };
      return { ...currentState, magnets: newMagnets };
    });
  }

  handleAddText() {
    // Use the FabricWrapper's addText method which handles both canvas and state
    this.workspace.fabricWrapper.addText('Hello');
  }

  handleTextPropertyChange(event) {
    const { property } = event.target.dataset;
    const { value } = event.target;

    if (!property) return;

    // Update the Fabric.js object directly first for immediate feedback
    this.workspace.fabricWrapper.updateTextProperty(property, value);

    // Update UI state to reflect the change
    this.store.dispatch(currentState => {
      const currentProps = currentState.ui.selectedTextProperties || {};
      return {
        ...currentState,
        ui: {
          ...currentState.ui,
          selectedTextProperties: {
            ...currentProps,
            [property]: value
          }
        }
      };
    });
  }

  handleCropModeToggle() {
    const { ui } = this.store.getState();
    
    if (ui.workspaceMode === 'cropping') {
      // Auto-apply crop when exiting crop mode
      this.workspace.handleApplyCrop();
    } else {
      // Enter crop mode
      this.store.dispatch(currentState => ({ 
        ...currentState, 
        ui: { ...currentState.ui, workspaceMode: 'cropping' } 
      }));
    }
  }

  handleCropCancel() {
    this.store.dispatch(currentState => {
      return { ...currentState, ui: { ...currentState.ui, workspaceMode: 'select' } };
    });
  }

  handleFontDropdownChange(fontFamily, label) {
    // Update the label
    if (this.fontDropdownLabel) {
      this.fontDropdownLabel.textContent = label;
      this.fontDropdownLabel.style.fontFamily = fontFamily;
    }
    // Update the selected text object if any
    const fw = this.workspace && this.workspace.fabricWrapper;
    if (fw && fw.fabricCanvas) {
      const activeObj = fw.fabricCanvas.getActiveObject();
      if (activeObj && activeObj.type === 'i-text') {
        activeObj.set('fontFamily', fontFamily);
        fw.fabricCanvas.renderAll();
        fw.syncTextObjectToState(activeObj);
      }
    }
    // Update UI state
    this.store.dispatch(currentState => {
      const ui = { ...currentState.ui };
      if (ui.selectedTextProperties) {
        ui.selectedTextProperties = {
          ...ui.selectedTextProperties,
          fontFamily: fontFamily
        };
      }
      return { ...currentState, ui };
    });
  }

  render(magnetState, uiState) {
    if (!magnetState || !uiState) return;
    const { orientation, } = magnetState;
    
    // Update orientation toggle buttons
    if (this.landscapeBtn && this.portraitBtn) {
      this.landscapeBtn.classList.toggle('active', orientation === 'landscape');
      this.portraitBtn.classList.toggle('active', orientation === 'portrait');
    }

    // Update crop mode button state
    if (this.cropModeBtn) { 
      this.cropModeBtn.classList.toggle('active', uiState.workspaceMode === 'cropping'); 
    }
    
    // Show/hide and update text controls based on selection state
    if (this.textControls) {
      // Use CSS display property for proper layout handling
      this.textControls.style.display = uiState.isTextSelected ? 'block' : 'none';
      
      if (uiState.isTextSelected && uiState.selectedTextProperties) {
        const props = uiState.selectedTextProperties;
        
        // Update all text control inputs with current values
        if (this.textInput) {
          this.textInput.value = props.text || '';
        }
        // Update custom font dropdown label and highlight
        if (this.fontDropdownLabel && props.fontFamily) {
          this.fontDropdownLabel.textContent = this.getFontDropdownLabel(props.fontFamily);
          this.fontDropdownLabel.style.fontFamily = props.fontFamily;
        }
        if (this.fontDropdownList) {
          Array.from(this.fontDropdownList.children).forEach(li => {
            li.classList.toggle('active', li.getAttribute('data-font-family') === props.fontFamily);
          });
        }
        if (this.fontSizeInput) {
          this.fontSizeInput.value = props.fontSize || 40;
          if (this.fontSizeValue) this.fontSizeValue.textContent = props.fontSize || 40;
          // Update slider fill on render
          const min = parseInt(this.fontSizeInput.min, 10);
          const max = parseInt(this.fontSizeInput.max, 10);
          const val = parseInt(this.fontSizeInput.value, 10);
          const percent = ((val - min) / (max - min)) * 100;
          this.fontSizeInput.style.setProperty('--slider-progress', percent + '%');
        }
        if (this.fontColorInput) {
          this.fontColorInput.value = props.fill || '#000000';
        }
        
        MMS_LOGGER.debug('🎨 Text controls updated with:', props);
      }
    }
    
    // Show/hide crop controls
    if (this.cropControls) {
      this.cropControls.style.display = uiState.workspaceMode === 'cropping' ? 'block' : 'none';
    }

    // Show/hide string color controls for ornaments
    if (this.stringColorControls && this.isOrnament) {
      // Show string color controls when editing an ornament (when activeMagnetId is set)
      const hasActiveMagnet = !!magnetState;
      this.stringColorControls.style.display = hasActiveMagnet ? 'block' : 'none';
      
      // Set active swatch based on current state
      if (hasActiveMagnet && magnetState.stringColor) {
        const currentColor = magnetState.stringColor;
        this.stringColorControls.querySelectorAll('.mms-string-swatch').forEach(swatch => {
          swatch.classList.toggle('active', swatch.dataset.color === currentColor);
        });
      }
      
      // Disable string color swatches when locked
      const isLocked = uiState.isLocked || false;
      if (hasActiveMagnet) {
        this.stringColorControls.querySelectorAll('.mms-string-swatch').forEach(swatch => {
          swatch.disabled = !!isLocked;
          swatch.style.opacity = isLocked ? '0.6' : '1';
          swatch.style.cursor = isLocked ? 'not-allowed' : 'pointer';
        });
      }
    }

    // Highlight pan/select mode buttons
    const panBtn = this.element.querySelector('.mms-pan-mode-btn');
    const selectBtn = this.element.querySelector('.mms-select-mode-btn');
    if (panBtn) panBtn.classList.toggle('active', uiState.workspaceMode === 'pan');
    if (selectBtn) selectBtn.classList.toggle('active', uiState.workspaceMode === 'select');

    // Context panel background, button style, and visibility logic
    const contextPanel = this.element.querySelector('.mms-context-panel-bg, #mms-context-panel');
    const textControls = this.element.querySelector('.mms-text-controls');
    const cropControls = this.element.querySelector('.mms-crop-controls');
    const stringColorControls = this.element.querySelector('#mms-string-color-controls');
    const applyBtns = this.element.querySelectorAll('.mms-apply-finish-btn');
    // Determine if edit controls are active
    // For ornaments: show context panel when editing (when magnetState exists)
    // For magnets: show context panel only when text is selected or crop mode is active
    const hasActiveMagnet = !!magnetState;
    const editActive = (uiState.isTextSelected && textControls && textControls.style.display !== 'none') ||
                      (uiState.workspaceMode === 'cropping' && cropControls && cropControls.style.display !== 'none') ||
                      (this.isOrnament && hasActiveMagnet);
    if (contextPanel) {
      if (editActive) {
        contextPanel.classList.add('mms-context-panel-active');
        contextPanel.style.display = '';
      } else {
        contextPanel.classList.remove('mms-context-panel-active');
        contextPanel.style.display = 'none';
      }
    }
    if (applyBtns) {
      applyBtns.forEach(btn => {
        if (editActive) {
          btn.classList.add('mms-btn-primary');
          btn.classList.remove('mms-btn-secondary');
        } else {
          btn.classList.remove('mms-btn-primary');
          btn.classList.add('mms-btn-secondary');
        }
      });
    }
  }

  getFontDropdownLabel(fontFamily) {
    // Map fontFamily to label for dropdown
    const map = {
      "Arial, Helvetica, sans-serif": "Arial",
      "Helvetica, Arial, sans-serif": "Helvetica",
      "'Montserrat', Arial, sans-serif": "Montserrat",
      "'Lato', Arial, sans-serif": "Lato",
      "'Open Sans', Arial, sans-serif": "Open Sans",
      "Georgia, serif": "Georgia",
      "'Merriweather', Georgia, serif": "Merriweather",
      "'Times New Roman', Times, serif": "Times New Roman",
      "'Pacifico', cursive": "Pacifico",
      "'Dancing Script', cursive": "Dancing Script",
      "'Courier New', Courier, monospace": "Courier New"
    };
    return map[fontFamily] || fontFamily.split(',')[0].replace(/['"]/g, '').trim();
  }
}

// --- 4. EditingWorkspace Class ---
class EditingWorkspace {
  constructor(element, store) {
    this.element = element;
    this.store = store;
    this.defaultStateEl = this.element.querySelector('.mms-editing-default-state');
    this.activeStateEl = this.element.querySelector('.mms-editing-active-state');
    this.magnetNumberSpan = this.element.querySelector('.mms-editing-magnet-number');
    this.closeBtn = this.element.querySelector('.mms-editing-close-btn');
    this.canvasEl = this.element.querySelector('.mms-workspace-canvas');
    this.canvasContainer = this.element.querySelector('.mms-workspace-canvas-container');
    this.toolPanelEl = this.element.querySelector('.mms-editing-active-state');
    this.fabricWrapper = null;
    this.toolPanel = null;
    this.init();
  }

  init() {
    this.showDefaultState();
    if (this.canvasEl) { this.fabricWrapper = new FabricWrapper(this.canvasEl, this.store); }
    if (this.toolPanelEl) { this.toolPanel = new ToolPanel(this.toolPanelEl, this.store, this); }
    // X (close) button discards edits and resets magnet to empty
    this.closeBtn.addEventListener('click', () => {
      const { activeMagnetId } = this.store.getState().ui;
      if (activeMagnetId) {
        this.store.dispatch(currentState => {
          const newMagnets = { ...currentState.magnets };
          const existingMagnet = currentState.magnets[activeMagnetId];
          const resetMagnet = {
            id: activeMagnetId,
            state: 'empty',
            orientation: 'landscape',
            originalImage: null,
            personalizedImage: null
          };
          // Preserve stringColor for ornaments
          if (window.personalizerApp?.isOrnament && existingMagnet?.stringColor) {
            resetMagnet.stringColor = existingMagnet.stringColor;
          } else if (window.personalizerApp?.isOrnament) {
            resetMagnet.stringColor = 'gold';
          }
          newMagnets[activeMagnetId] = resetMagnet;
          return {
            ...currentState,
            magnets: newMagnets,
            ui: { ...currentState.ui, activeMagnetId: null }
          };
        });
        if (this.fabricWrapper) {
          this.fabricWrapper.workingImageUrl = null;
          this.fabricWrapper.clearCanvas();
        }
        // On mobile, switch back to 'Your Magnets' tab
        if (window.innerWidth <= 768) {
          const app = window.personalizerApp;
          if (app && app.switchToTab) {
            app.switchToTab('magnets');
          }
        }
      }
    });
    this.applyFinishBtn = this.element.querySelector('.mms-apply-finish-btn');
    if (this.applyFinishBtn) {
      this.applyFinishBtn.addEventListener('click', () => this.handleApplyAndFinish());
    }
  }

  showDefaultState() {
    this.defaultStateEl.style.display = 'flex';
    this.activeStateEl.style.display = 'none';
  }

  showActiveState(magnetId) {
    this.defaultStateEl.style.display = 'none';
    this.activeStateEl.style.display = 'flex';
    this.magnetNumberSpan.textContent = magnetId;
  }

  render(state) {
    const { activeMagnetId } = state.ui;
    if (activeMagnetId) {
      const activeMagnetState = state.magnets[activeMagnetId];
      this.showActiveState(activeMagnetId);
      if (window.innerWidth <= 768) {
        const app = window.personalizerApp;
        if (app && app.switchToTab) {
          app.switchToTab('editing');
        }
      }
      if (this.canvasContainer) {
        this.canvasContainer.classList.toggle('horizontal', activeMagnetState.orientation === 'landscape');
        this.canvasContainer.classList.toggle('vertical', activeMagnetState.orientation === 'portrait');
        if (this.fabricWrapper) {
          this.fabricWrapper.updateCanvasOrientation(activeMagnetState.orientation);
        }
      }
      // Always prefer workingImageUrl if present
      let imageToLoad;
      if (this.fabricWrapper && this.fabricWrapper.workingImageUrl) {
        imageToLoad = this.fabricWrapper.workingImageUrl;
        MMS_LOGGER.debug('[Render Debug] Using workingImageUrl for editing');
      } else {
        imageToLoad = activeMagnetState.originalImage;
        MMS_LOGGER.debug('[Render Debug] Using originalImage for editing');
      }
      if (this.fabricWrapper.currentImageId !== activeMagnetId || !this.fabricWrapper.imageObject) {
        MMS_LOGGER.debug('[Render Debug] Loading image for magnet', activeMagnetId, imageToLoad);
        this.fabricWrapper.loadImage(imageToLoad, activeMagnetId);
      }
      this.toolPanel.render(activeMagnetState, state.ui);
      this.fabricWrapper.render(activeMagnetState);
      this.fabricWrapper.setInteractionMode(state.ui.workspaceMode);
      if (state.ui.workspaceMode === 'cropping') { this.fabricWrapper.enterCropMode(); } else { this.fabricWrapper.exitCropMode(); }
    } else {
      this.showDefaultState();
      if (this.fabricWrapper) {
        this.fabricWrapper.workingImageUrl = null; // Clear working image when session ends
      }
    }
  }

  handleApplyCrop() {
    const croppedDataUrl = this.fabricWrapper.applyCrop();
    if (!croppedDataUrl) {
              MMS_LOGGER.debug('[Crop Debug] No cropped data URL returned');
      return;
    }
    const { activeMagnetId } = this.store.getState().ui;
    if (!activeMagnetId) {
              MMS_LOGGER.debug('[Crop Debug] No activeMagnetId');
      return;
    }
    // Aggressively clear the canvas and all objects before loading the cropped image
    this.fabricWrapper.fabricCanvas.clear();
    this.fabricWrapper.cropRect = null;
    this.fabricWrapper.imageObject = null;
    this.fabricWrapper.workingImageUrl = croppedDataUrl;
    this.fabricWrapper.loadImage(croppedDataUrl, activeMagnetId);
    this.fabricWrapper.exitCropMode(); // Ensure crop rectangle is removed and canvas is clean
    this.store.dispatch(currentState => ({
      ...currentState,
      ui: { ...currentState.ui, workspaceMode: 'select' }
    }));
  }

  handleApplyAndFinish() {
    const { activeMagnetId } = this.store.getState().ui;
            MMS_LOGGER.debug(`🎯 [Apply & Finish] Starting for Magnet ${activeMagnetId}`);
    
    const finalImageData = this.fabricWrapper.getFinalImageData();
    if (!finalImageData) {
      console.error('❌ [Apply & Finish] Failed to capture final image data');
      return;
    }
    
            MMS_LOGGER.debug('✅ [Apply & Finish] Captured final image data, updating state...');
    this.store.dispatch(currentState => {
      if (!activeMagnetId) {
        console.error('❌ [Apply & Finish] No active magnet ID found');
        return currentState;
      }
      
      const beforeState = currentState.magnets[activeMagnetId];
      StateValidator.validateMagnetState(activeMagnetId, beforeState, 'Before Apply & Finish');
      
      const newMagnets = { ...currentState.magnets };
      const currentMagnet = newMagnets[activeMagnetId];
      const settledMagnet = {
        id: currentMagnet.id,
        state: 'settled',
        orientation: currentMagnet.orientation,
        originalImage: currentMagnet.originalImage,
        personalizedImage: finalImageData
      };
      // Preserve stringColor for ornaments
      if (window.personalizerApp?.isOrnament && currentMagnet.stringColor) {
        settledMagnet.stringColor = currentMagnet.stringColor;
      } else if (window.personalizerApp?.isOrnament) {
        settledMagnet.stringColor = 'gold';
      }
      newMagnets[activeMagnetId] = settledMagnet;
      
      const newState = { 
        ...currentState, 
        magnets: newMagnets, 
        ui: { 
          ...currentState.ui, 
          activeMagnetId: null, 
          workspaceMode: 'select',
          isTextSelected: false,
          selectedTextId: null,
          selectedTextProperties: null,
        } 
      };
      
      StateValidator.logStateTransition(activeMagnetId, beforeState, newMagnets[activeMagnetId], 'Apply & Finish');
              MMS_LOGGER.debug(`✅ [Apply & Finish] Updated magnet ${activeMagnetId} with final image`);
      
      // After updating state and UI, clear workingImageUrl
      if (this.fabricWrapper) {
        this.fabricWrapper.workingImageUrl = null;
      }
      
      return newState;
    });
    
    // On mobile, switch back to "Your Magnets" tab to show the completed magnet
    if (window.innerWidth <= 768) {
      const app = window.personalizerApp;
      if (app && app.switchToTab) {
        MMS_LOGGER.debug('📱 [Apply & Finish] Switching back to Your Magnets tab on mobile');
        app.switchToTab('magnets');
      }
    }
  }
}

// --- 5. MagnetCard Class ---
class MagnetCard {
  constructor(element, store, magnetId) {
    this.element = element;
    this.store = store;
    this.id = magnetId;
    this.preview = this.element.querySelector('.mms-magnet-preview');
    this.input = document.querySelector(`.mms-placeholder-input[data-idx="${this.id}"]`);
    this.imageContainer = this.preview.querySelector('.mms-image-container');
    this.previewImage = this.preview.querySelector('.mms-preview-image');
    this.loadingState = this.preview.querySelector('.mms-loading-state');
    this.settledPanel = this.preview.querySelector('.mms-settled-panel');
    this.init();
  }

  init() {
    this.preview.addEventListener('click', (event) => this.handlePreviewClick(event));
    this.input.addEventListener('change', (event) => this.handleFileSelect(event));
    this.preview.addEventListener('dragover', (event) => this.handleDragOver(event));
    this.preview.addEventListener('dragleave', (event) => this.handleDragLeave(event));
    this.preview.addEventListener('drop', (event) => this.handleDrop(event));
    this.initControls();
  }

  handlePreviewClick(event) {
    // Ignore clicks on string color swatches - they have their own handlers
    // Prevents image picker from opening when clicking string color swatches
    if (event && event.target && (
      event.target.closest('.mms-string-color-selector') ||
      event.target.closest('.mms-string-swatch')
    )) {
      return;
    }
    
    // If editing is in progress, discard edits for previous magnet
    const { activeMagnetId } = this.store.getState().ui;
    if (activeMagnetId && activeMagnetId !== this.id) {
      this.store.dispatch(currentState => {
        const newMagnets = { ...currentState.magnets };
        const existingMagnet = currentState.magnets[activeMagnetId];
        const resetMagnet = {
          id: activeMagnetId,
          state: 'empty',
          orientation: 'landscape',
          originalImage: null,
          personalizedImage: null
        };
        // Preserve stringColor for ornaments
        if (window.personalizerApp?.isOrnament && existingMagnet?.stringColor) {
          resetMagnet.stringColor = existingMagnet.stringColor;
        } else if (window.personalizerApp?.isOrnament) {
          resetMagnet.stringColor = 'gold';
        }
        newMagnets[activeMagnetId] = resetMagnet;
        return {
          ...currentState,
          magnets: newMagnets,
          ui: { ...currentState.ui, activeMagnetId: null }
        };
      });
      if (window.personalizerApp && window.personalizerApp.editingWorkspace && window.personalizerApp.editingWorkspace.fabricWrapper) {
        window.personalizerApp.editingWorkspace.fabricWrapper.workingImageUrl = null;
        window.personalizerApp.editingWorkspace.fabricWrapper.clearCanvas();
      }
    }
    const magnetState = this.store.state.magnets[this.id];
    if (magnetState.state === 'empty') { this.input.click(); }
  }

  handleFileSelect(event) {
    const file = event.target.files[0];
    if (!file) return;

            MMS_LOGGER.debug(`📁 [File Upload] Starting upload for Magnet ${this.id}`);
    const reader = new FileReader();
    reader.onload = () => {
       this.store.dispatch(currentState => {
        const beforeState = currentState.magnets[this.id];
        const newMagnets = { ...currentState.magnets };
        newMagnets[this.id] = {
          ...currentState.magnets[this.id],
          state: 'editing', // Not settled until Apply & Finish
          originalImage: reader.result,
          personalizedImage: null, // Clear any previous finalized image
          orientation: 'landscape'
        };
        
        const newState = {
          ...currentState,
          magnets: newMagnets,
          ui: {
            ...currentState.ui,
            activeMagnetId: this.id,
            workspaceMode: 'select'
          }
        };
        
        StateValidator.logStateTransition(this.id, beforeState, newMagnets[this.id], 'File Upload');
        
        return newState;
      });
    };
    reader.readAsDataURL(file);
    // Reset file input to allow uploading the same file again
    event.target.value = '';
    updateAddToCartButton(this.store);
            MMS_LOGGER.debug('[MagnetCard.handleFileSelect] Image uploaded for magnet', this.id);
  }

  handleDragOver(event) {
    event.preventDefault();
    if (this.store.getState().magnets[this.id].state === 'empty') {
      this.preview.classList.add('mms-drop-target-active');
    }
  }

  handleDragLeave(event) {
    event.preventDefault();
    this.preview.classList.remove('mms-drop-target-active');
  }

  handleDrop(event) {
    event.preventDefault();
    this.preview.classList.remove('mms-drop-target-active');
    if (this.store.getState().magnets[this.id].state === 'empty') {
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        this.handleImageUpload(file);
      }
    }
  }

  handleImageUpload(file) {
    const objectURL = URL.createObjectURL(file);
    this.store.dispatch(currentState => {
      const newMagnets = { ...currentState.magnets };
      newMagnets[this.id] = { ...newMagnets[this.id], state: 'loading', originalImage: objectURL };
      return { ...currentState, magnets: newMagnets };
    });
    const tempImage = new Image();
    tempImage.onload = () => {
      this.store.dispatch(currentState => {
        const newMagnets = { ...currentState.magnets };
        newMagnets[this.id] = { ...newMagnets[this.id], state: 'settled' };
        return { ...currentState, magnets: newMagnets };
      });
    };
    tempImage.src = objectURL;
    updateAddToCartButton(this.store);
            MMS_LOGGER.debug('[MagnetCard.handleImageUpload] Image uploaded for magnet', this.id);
  }

  initControls() {
    // Only add event listener for Delete (trash) button
    const deleteBtn = this.element.querySelector('.mms-delete-magnet-btn');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent triggering upload dialog
        // Reset state to empty, clear images
        this.store.dispatch(currentState => {
          const newMagnets = { ...currentState.magnets };
          const existingMagnet = currentState.magnets[this.id];
          const resetMagnet = {
            id: this.id,
            state: 'empty',
            orientation: 'landscape',
            originalImage: null,
            personalizedImage: null
          };
          // Preserve stringColor for ornaments
          if (window.personalizerApp?.isOrnament && existingMagnet?.stringColor) {
            resetMagnet.stringColor = existingMagnet.stringColor;
          } else if (window.personalizerApp?.isOrnament) {
            resetMagnet.stringColor = 'gold';
          }
          newMagnets[this.id] = resetMagnet;
          return {
            ...currentState,
            magnets: newMagnets,
            ui: { ...currentState.ui, activeMagnetId: null }
          };
        });
        // Clear working image and session state for a true reset
        if (window.personalizerApp && window.personalizerApp.editingWorkspace && window.personalizerApp.editingWorkspace.fabricWrapper) {
          window.personalizerApp.editingWorkspace.fabricWrapper.workingImageUrl = null;
          window.personalizerApp.editingWorkspace.fabricWrapper.clearCanvas();
        }
        // Do NOT trigger file upload dialog here
        updateAddToCartButton(this.store);
      });
    }
  }

  handleRemoveImageClick() {
    this.store.dispatch(currentState => {
      const newMagnets = { ...currentState.magnets };
      const existingMagnet = currentState.magnets[this.id];
      const updatedMagnet = { ...existingMagnet, state: 'empty', originalImage: null, personalizedImage: null };
      // Preserve stringColor for ornaments
      if (!window.personalizerApp?.isOrnament || !existingMagnet?.stringColor) {
        if (window.personalizerApp?.isOrnament) {
          updatedMagnet.stringColor = 'gold';
        }
      }
      newMagnets[this.id] = updatedMagnet;
      const newActiveMagnetId = currentState.ui.activeMagnetId === this.id ? null : currentState.ui.activeMagnetId;
      return { ...currentState, magnets: newMagnets, ui: { ...currentState.ui, activeMagnetId: newActiveMagnetId } };
    });
    updateAddToCartButton(this.store);
            MMS_LOGGER.debug('[MagnetCard.handleRemoveImageClick] Image removed for magnet', this.id);
  }

  render(state) {
    if (!state) {
      // Hide the card if state is undefined (should not be visible)
      this.element.classList.add('hidden');
      return;
    }
            MMS_LOGGER.debug(`🖼️ [MagnetCard Render] Magnet ${this.id} - State: ${state.state}, Orientation: ${state.orientation}`);
    StateValidator.validateMagnetState(this.id, state, 'MagnetCard Render');
    
    this.preview.dataset.state = state.state;
    this.loadingState.hidden = state.state !== 'loading';
    const showImage = state.state === 'settled' || state.state === 'loaded';
    this.imageContainer.hidden = !showImage;
    this.settledPanel.hidden = state.state !== 'settled';
    
    // Update magnet frame orientation
    const magnetFrame = this.preview.querySelector('.mms-magnet-frame');
    if (magnetFrame && state.orientation) {
      // Remove existing orientation classes
      magnetFrame.classList.remove('horizontal', 'vertical', 'landscape', 'portrait');
      
      // Add new orientation class - map both naming conventions
      if (state.orientation === 'landscape' || state.orientation === 'horizontal') {
        magnetFrame.classList.add('horizontal');
      } else if (state.orientation === 'portrait' || state.orientation === 'vertical') {
        magnetFrame.classList.add('vertical');
      }
      
              MMS_LOGGER.debug(`🖼️ [MagnetCard Orientation] Magnet ${this.id} - Set frame to ${state.orientation}`);
    }
    
    // Image priority: personalizedImage (final) > originalImage (source)
    const imageToShow = state.personalizedImage || state.originalImage;
            MMS_LOGGER.debug(`🖼️ [MagnetCard Image] Magnet ${this.id} - Using ${state.personalizedImage ? 'personalized' : 'original'} image`);
    
    if (imageToShow && this.previewImage.src !== imageToShow) {
      this.previewImage.src = imageToShow;
    } else if (!imageToShow) {
      this.previewImage.src = '';
    }
    
    // Update string color selector for ornaments
    if (window.personalizerApp?.isOrnament && state.stringColor) {
      const selector = this.element.querySelector('.mms-string-color-selector');
      if (selector) {
        selector.dataset.stringColor = state.stringColor;
        selector.querySelectorAll('.mms-string-swatch').forEach(swatch => {
          swatch.classList.toggle('active', swatch.dataset.color === state.stringColor);
        });
      }
    }
  }

  setEditingLocked(locked) {
    // Disable/enable file input
    const fileInput = this.element.querySelector('input[type="file"]');
    if (fileInput) fileInput.disabled = !!locked;
    // Disable/enable all delete/remove buttons
    const removeBtns = this.element.querySelectorAll('.mms-remove-image-btn, .mms-delete-magnet-btn');
    removeBtns.forEach(btn => btn.disabled = !!locked);
    // Optionally, disable drag-and-drop and clickable preview
    const preview = this.element.querySelector('.mms-magnet-preview');
    if (preview) {
      if (locked) {
        preview.style.pointerEvents = 'none';
        preview.style.opacity = '0.6';
      } else {
        preview.style.pointerEvents = '';
        preview.style.opacity = '';
      }
    }
    // Disable string color swatches for ornaments when locked
    // Prevents string color changes after personalization is locked
    if (window.personalizerApp?.isOrnament) {
      const swatches = this.element.querySelectorAll('.mms-string-color-selector .mms-string-swatch');
      swatches.forEach(swatch => {
        swatch.disabled = !!locked;
        swatch.style.opacity = locked ? '0.6' : '1';
        swatch.style.cursor = locked ? 'not-allowed' : 'pointer';
      });
    }
    // Add/remove locked class for visual feedback
    if (locked) {
      this.element.classList.add('locked');
    } else {
      this.element.classList.remove('locked');
    }
  }
}

// Add comprehensive state validation and logging methods
class StateValidator {
  static validateMagnetState(magnetId, magnetState, context = '') {
            MMS_LOGGER.debug(`🔍 [State Validation] ${context} - Magnet ${magnetId}:`, {
      state: magnetState?.state,
      hasOriginalImage: !!magnetState?.originalImage,
      hasPersonalizedImage: !!magnetState?.personalizedImage,
      textObjectsCount: magnetState?.textObjects?.length || 0,
      textObjects: magnetState?.textObjects,
      orientation: magnetState?.orientation
    });
    
    // Validation rules
    const validations = [];
    
    if (magnetState?.state === 'settled' && !magnetState?.originalImage) {
      validations.push(`❌ State 'settled' but no originalImage`);
    }
    
    if (magnetState?.textObjects && magnetState?.textObjects.length > 0 && !magnetState?.personalizedImage) {
      validations.push(`⚠️ Has text objects but no personalizedImage (will be generated on Apply & Finish)`);
    }
    
    if (magnetState?.personalizedImage && !magnetState?.originalImage) {
      validations.push(`❌ Has personalizedImage but no originalImage`);
    }
    
    if (validations.length > 0) {
      console.warn(`🚨 [State Issues] Magnet ${magnetId}:`, validations);
    } else {
              MMS_LOGGER.debug(`✅ [State Valid] Magnet ${magnetId}`);
    }
    
    return validations;
  }
  
  static validateStateSync(store, magnetId, action = 'unknown') {
            MMS_LOGGER.debug(`🔄 [State Sync Check] After ${action} - Magnet ${magnetId}`);
    const state = store.getState();
    const magnetState = state.magnets[magnetId];
    
    if (!magnetState) {
      console.error(`❌ [State Sync Error] Magnet ${magnetId} not found in state`);
      return false;
    }
    
    this.validateMagnetState(magnetId, magnetState, `After ${action}`);
    return true;
  }
  
  static logStateTransition(magnetId, before, after, action) {
            MMS_LOGGER.debug(` [State Transition] Magnet ${magnetId} - ${action}:`);
        MMS_LOGGER.debug('  Before:', {
      state: before?.state,
      hasOriginalImage: !!before?.originalImage,
      hasPersonalizedImage: !!before?.personalizedImage,
      textObjectsCount: before?.textObjects?.length || 0
    });
            MMS_LOGGER.debug('  After:', {
      state: after?.state,
      hasOriginalImage: !!after?.originalImage,
      hasPersonalizedImage: !!after?.personalizedImage,
      textObjectsCount: after?.textObjects?.length || 0
    });
  }
}

// --- 6. PersonalizerApp Class ---
class PersonalizerApp {
  constructor() {
            MMS_LOGGER.debug('🚀 [PersonalizerApp] Constructor starting...');
    this.store = new Store((state, action) => {
      // This is a thunk-based reducer.
      // It assumes the action itself is a function that returns the new state.
      // Or it's a simple object action that will be handled by a switch.
      if (typeof action === 'function') {
        return action(state);
      }
      
      // Handle object-based actions for text selection
      switch (action.type) {
        case 'TEXT_OBJECT_SELECTED':
          return {
            ...state,
            ui: {
              ...state.ui,
              isTextSelected: true,
              selectedTextId: action.payload.id,
              selectedTextProperties: action.payload,
            },
          };
        case 'TEXT_OBJECT_DESELECTED':
          return {
            ...state,
            ui: {
              ...state.ui,
              isTextSelected: false,
              selectedTextId: null,
              selectedTextProperties: null,
            },
          };
        default:
          return state;
      }
    }, {
      product: { variantId: null, magnetCount: 0 },
      magnets: {},
      ui: {
        activeMagnetId: null,
        workspaceMode: 'select', // Default to select mode (pan + zoom + selection enabled)
        isCartButtonEnabled: false,
        isTextSelected: false,
        selectedTextId: null,
        selectedTextProperties: null,
        isLocked: false, // <-- NEW
      },
    });
    // Detect product type for ornament support
    // For ornaments: product.type == "Christmas Ornament" in Liquid, passed as data-product-type="ornament"
    // For magnets: data-product-type="magnet" (default)
    const personalizerEl = document.querySelector('#mms-personaliser, .mms-personaliser-wrapper');
    this.isOrnament = personalizerEl && personalizerEl.dataset.productType === 'ornament';
    this.init();
  }

  /**
   * Main initialization function.
   * Kicks off all setup processes.
   */
  init() {
            MMS_LOGGER.debug('🚀 MMS Personalizer App Initializing...');
    document.addEventListener('DOMContentLoaded', () => {
      const personalizerContainer = document.querySelector('.mms-personaliser-wrapper');
      if (!personalizerContainer) {
        MMS_LOGGER.debug('MMS Personalizer: Container not found. Not initializing on this page.');
        return;
      }

      this.initializeState();
      this.store.subscribe((state) => {
        this.render(state);
        updateAddToCartButton(this.store);
        MMS_LOGGER.debug('[store.subscribe] Called updateAddToCartButton after state change');
      });
      this.initComponents();
      this.initVariantHandling();
      this.initStringColorHandlers();
              MMS_LOGGER.debug('✅ MMS Personalizer App Ready.');
      // Add lock toggle event listener
      const lockToggleDiv = document.getElementById('personalization-lock-toggle');
      const lockCheckbox = document.getElementById('personalization-lock-checkbox');
      if (lockCheckbox) {
        lockCheckbox.checked = false;
        lockCheckbox.addEventListener('change', (e) => {
          const checked = e.target.checked;
          this.store.dispatch((state) => ({
            ...state,
            ui: {
              ...state.ui,
              isLocked: checked
            }
          }));
          updateAddToCartButton(this.store);
          this.toggleEditingLocked(checked);
        });
      }
    });
  }

  /**
   * Sets the initial state of the application based on the DOM.
   */
  initializeState() {
    // Select only the magnet cards that are NOT hidden
    const magnetElements = document.querySelectorAll('.mms-magnet-card:not(.hidden)');
    const initialState = {
      product: { variantId: null, magnetCount: magnetElements.length },
      magnets: {},
      ui: {
        activeMagnetId: null,
        workspaceMode: 'select', // Default to select mode (pan + zoom + selection enabled)
        isCartButtonEnabled: false,
        isTextSelected: false,
        selectedTextId: null,
        selectedTextProperties: null,
        isLocked: false, // Initialize isLocked to false
      },
    };

    magnetElements.forEach(el => {
      const magnetId = parseInt(el.dataset.magnet, 10);
      const magnetState = {
        id: magnetId,
        state: 'empty', // 'empty', 'loading', 'settled'
        orientation: 'landscape',
        originalImage: null,
        personalizedImage: null
      };
      // Add string color state for ornaments (default: 'gold')
      // Only added for ornament products, not for magnets
      if (this.isOrnament) {
        magnetState.stringColor = 'gold';
      }
      initialState.magnets[magnetId] = magnetState;
    });
    
    // Use a special dispatch to set the initial state without notifying
    // listeners yet, as they haven't been set up.
    this.store.state = initialState; 
    updateAddToCartButton(this.store);
            MMS_LOGGER.debug('[initializeState] Called after setting initial state');
  }

  /**
   * Finds all component elements in the DOM and initializes them.
   */
  initComponents() {
    this.magnetCards = [];
    // Select only the magnet cards that are NOT hidden
    const magnetElements = document.querySelectorAll('.mms-magnet-card:not(.hidden)');
            MMS_LOGGER.debug(`🧩 [Components Init] Found ${magnetElements.length} magnet elements`);
    
    magnetElements.forEach(el => {
      const magnetId = parseInt(el.dataset.magnet, 10);
              MMS_LOGGER.debug(`🧩 [Components Init] Creating MagnetCard for ID: ${magnetId}`);
      this.magnetCards.push(new MagnetCard(el, this.store, magnetId));
    });

    // Editing Workspace
    const workspaceEl = document.querySelector('.mms-editing-panel');
            MMS_LOGGER.debug(`🧩 [Components Init] Workspace element found: ${!!workspaceEl}`);
    if (workspaceEl) {
      this.workspace = new EditingWorkspace(workspaceEl, this.store);
    }
    
    // Initialize mobile tabs
    this.initMobileTabs();
    
            MMS_LOGGER.debug(`🧩 [Components Init] Initialized ${this.magnetCards.length} magnet cards`);
  }

  /**
   * Initialize mobile tab functionality
   */
  initMobileTabs() {
    const tabButtons = document.querySelectorAll('.mms-tab-btn');
    const tabContents = document.querySelectorAll('.mms-tab-content');
    
            MMS_LOGGER.debug(`📱 [Mobile Tabs] Found ${tabButtons.length} tab buttons and ${tabContents.length} tab contents`);
    
    tabButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const targetTab = button.getAttribute('data-tab');
        MMS_LOGGER.debug(`📱 [Mobile Tabs] Switching to tab: ${targetTab}`);
        this.switchToTab(targetTab);
      });
    });
  }

  /**
   * Switch to a specific tab
   */
  switchToTab(tabName) {
    const tabButtons = document.querySelectorAll('.mms-tab-btn');
    const tabContents = document.querySelectorAll('.mms-tab-content');
    
    // Update tab buttons
    tabButtons.forEach(btn => {
      btn.classList.toggle('active', btn.getAttribute('data-tab') === tabName);
    });
    
    // Update tab contents
    tabContents.forEach(content => {
      content.classList.toggle('active', content.getAttribute('data-tab-content') === tabName);
    });
    
            MMS_LOGGER.debug(`📱 [Mobile Tabs] Switched to tab: ${tabName}`);
  }

  /**
   * Extract magnet count from variant title or option values
   */
  extractMagnetCount(variant) {
    if (!variant) return 1;
    
            MMS_LOGGER.debug('🔍 [Variant] Extracting magnet count from:', variant);
    
    // Check variant title for number patterns
    const title = variant.title || '';
            MMS_LOGGER.debug('📝 [Variant] Title:', title);
    
    // Try multiple patterns for flexibility
    // Supports both magnet and ornament variant naming conventions
    const patterns = [
      /(\d+)\s*magnet/i,           // "1 magnet", "3 magnets" (magnets)
      /(\d+)\s*ornament/i,         // "1 ornament", "3 ornaments" (ornaments - NEW)
      /(\d+)\s*pack/i,             // "1 pack", "3 pack" (generic pack)
      /(\d+)\s*piece/i,            // "1 piece", "3 pieces" (generic piece)
      /^(\d+)$/,                   // Just a number (fallback)
      /(\d+)\s*x/i,                // "1x", "3x" (alternative format)
    ];
    
    for (let pattern of patterns) {
      const titleMatch = title.match(pattern);
      if (titleMatch) {
        const count = parseInt(titleMatch[1]);
        MMS_LOGGER.debug(`✅ [Variant] Found magnet count ${count} from title pattern: ${pattern}`);
        return count;
      }
    }
    
    // Check option values for quantity indicators
    if (variant.options) {
              MMS_LOGGER.debug('🎛️ [Variant] Checking options:', variant.options);
      for (let option of variant.options) {
        for (let pattern of patterns) {
          const optionMatch = String(option).match(pattern);
          if (optionMatch) {
            const count = parseInt(optionMatch[1]);
            MMS_LOGGER.debug(`✅ [Variant] Found magnet count ${count} from option pattern: ${pattern}`);
            return count;
          }
        }
      }
    }
    
    // Check option1, option2, option3 directly (Shopify variant structure)
    ['option1', 'option2', 'option3'].forEach((optionKey) => {
      if (variant[optionKey]) {
        MMS_LOGGER.debug(`🎛️ [Variant] Checking ${optionKey}:`, variant[optionKey]);
        for (let pattern of patterns) {
          const optionMatch = String(variant[optionKey]).match(pattern);
          if (optionMatch) {
            const count = parseInt(optionMatch[1]);
            MMS_LOGGER.debug(`✅ [Variant] Found magnet count ${count} from ${optionKey} pattern: ${pattern}`);
            return count;
          }
        }
      }
    });
    
            MMS_LOGGER.debug('⚠️ [Variant] No magnet count pattern found, defaulting to 1');
    // Default to 1 magnet if no pattern found
    return 1;
  }

  /**
   * Handle variant change events
   */
  handleVariantChange(event) {
            MMS_LOGGER.debug('🔄 [Variant] Change detected:', event);
    const variant = event.data?.variant;
    if (!variant) return;
    
    const magnetCount = this.extractMagnetCount(variant);
    this.updateMagnetCards(magnetCount);
  }

  /**
   * Update visible magnet cards based on count
   */
  updateMagnetCards(count) {
            MMS_LOGGER.debug(`🔄 [Variant] Updating magnet cards to show ${count}`);
    const magnetCards = document.querySelectorAll('.mms-magnet-card');
    const currentVisibleCount = document.querySelectorAll('.mms-magnet-card:not(.hidden)').length;
    
    // Only update if the count has actually changed
    if (count === currentVisibleCount) {
              MMS_LOGGER.debug(`🔄 [Variant] Count unchanged (${count}), skipping update`);
      return;
    }
    
    // Get current state before any changes
    const currentState = this.store.getState();
    const currentMagnets = { ...currentState.magnets };
    
    // Track which magnets should be retained/created/hidden
    const newMagnetsState = {};
    
    magnetCards.forEach((card, index) => {
      const magnetNumber = index + 1;
      const magnetId = parseInt(card.dataset.magnet, 10);
      
      if (magnetNumber <= count) {
        card.classList.remove('hidden');
        
        // Defensive check: If a magnet previously existed but is being re-initialized, log a warning
        if (!currentMagnets[magnetId] && newMagnetsState[magnetId]) {
          console.warn(`[updateMagnetCards] WARNING: Magnet ${magnetId} existed but is being re-initialized as empty!`);
        }
        
        // Preserve existing magnet data if it exists
        if (currentMagnets[magnetId]) {
          newMagnetsState[magnetId] = currentMagnets[magnetId];
        } else {
          // Initialize new magnet with default state
          const newMagnetState = {
            id: magnetId,
            state: 'empty',
            orientation: 'landscape',
            originalImage: null,
            personalizedImage: null
          };
          // Add stringColor for ornaments
          if (this.isOrnament) {
            newMagnetState.stringColor = 'gold';
          }
          newMagnetsState[magnetId] = newMagnetState;
          MMS_LOGGER.debug(`[updateMagnetCards] Initialized new magnet ${magnetId} as empty`);
        }
      } else {
        card.classList.add('hidden');
        // Defensive: Do NOT delete or reset state for hidden magnets
        if (currentMagnets[magnetId]) {
          newMagnetsState[magnetId] = currentMagnets[magnetId];
        }
      }
    });
    
    // Defensive: Log the new state for debugging
            MMS_LOGGER.debug('[updateMagnetCards] New magnets state:', newMagnetsState);
    
    // Update the product info in state
    const visibleIds = Object.keys(newMagnetsState).map(Number);
    this.store.dispatch(currentState => {
      let newUi = { ...currentState.ui };
      if (!visibleIds.includes(currentState.ui.activeMagnetId)) {
        newUi.activeMagnetId = null;
      }
      return {
        ...currentState,
        product: {
          ...currentState.product,
          magnetCount: count
        },
        magnets: newMagnetsState,
        ui: newUi
      };
    });
    
    // Initialize new MagnetCard components only for newly visible cards
    this.initNewComponents(count);
    
            MMS_LOGGER.debug(`🎯 [Variant] Updated magnet display: showing ${count} magnet(s)`);
    updateAddToCartButton(this.store);
            MMS_LOGGER.debug('[updateMagnetCards] Called after updating visible cards');
    // After updating visible cards and state:
    // Remove MagnetCard instances for hidden magnets
    this.magnetCards = this.magnetCards.filter(card => {
      return !!newMagnetsState[card.id];
    });
  }
  
  /**
   * Initialize only new components that weren't visible before
   */
  initNewComponents(count) {
    // Find which MagnetCard IDs we already have initialized
    const initializedIds = this.magnetCards.map(card => card.id);
    
    // Check for cards that need initialization
    const magnetElements = document.querySelectorAll('.mms-magnet-card:not(.hidden)');
    magnetElements.forEach(el => {
      const magnetId = parseInt(el.dataset.magnet, 10);
      if (!initializedIds.includes(magnetId)) {
        MMS_LOGGER.debug(`🧩 [Components Init] Creating new MagnetCard for ID: ${magnetId}`);
        this.magnetCards.push(new MagnetCard(el, this.store, magnetId));
      }
    });
  }

  /**
   * Initialize variant handling
   */
  initVariantHandling() {
    // Listen for variant changes using pub/sub if available
    if (window.subscribe && window.PUB_SUB_EVENTS) {
              MMS_LOGGER.debug('🔄 [Variant] Using pub/sub for variant changes');
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
    } else if (typeof subscribe !== 'undefined' && PUB_SUB_EVENTS) {
              MMS_LOGGER.debug('🔄 [Variant] Using global pub/sub for variant changes');
      subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
    } else {
              MMS_LOGGER.debug('🔄 [Variant] Using fallback variant change detection');
      this.setupVariantChangeListener();
    }
    
    // Listen for custom variant change events from magnet pack selection
    document.addEventListener('variant:change', (event) => {
              MMS_LOGGER.debug('🔄 [Variant] Custom variant change event received:', event.detail);
      const { variantId, magnetCount } = event.detail;
      
      // Create a proper variant object that extractMagnetCount can handle
      const variant = {
        id: variantId,
        title: `${magnetCount} magnet${magnetCount > 1 ? 's' : ''}`,
        option1: magnetCount.toString()
      };
      
      // Update the cart actions form variant ID to match the selected variant
      const cartForm = document.querySelector('[id^="MmsCartActions-"] form[data-type="add-to-cart-form"]');
      if (cartForm) {
        const cartVariantInput = cartForm.querySelector('input[name="id"]');
        if (cartVariantInput) {
          cartVariantInput.value = variantId;
          MMS_LOGGER.debug('🔄 [Variant] Updated cart form variant ID to:', variantId);
        }
      }
      
      // Call updateMagnetCards directly with the count
      this.updateMagnetCards(magnetCount);
    });
  }

  /**
   * Fallback variant change detection
   */
  setupVariantChangeListener() {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) {
      console.warn('variant-selects element not found');
      return;
    }
    
    // Listen for input changes in variant selects
    variantSelects.addEventListener('change', () => this.handleVariantSelectChange());
    
    // Also use MutationObserver to watch for variant script changes
    const variantScript = variantSelects.querySelector('[data-selected-variant]');
    if (variantScript) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            this.handleVariantScriptChange();
          }
        });
      });
      
      observer.observe(variantScript, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
    
            MMS_LOGGER.debug('🔄 [Variant] Fallback listeners initialized');
  }

  /**
   * Initialize string color change handlers for ornaments
   * 
   * Handles string color selection in two locations:
   * 1. Preview panel (magnet card) - string color selector below preview
   * 2. Editing workspace - string color controls in context panel
   * 
   * Both locations sync with each other and update the magnet state.
   * Event propagation is stopped to prevent triggering image picker.
   */
  initStringColorHandlers() {
    if (!this.isOrnament) return; // Only for ornament products
    
    // Event delegation for string color swatches in preview panel
    document.addEventListener('click', (e) => {
      const swatch = e.target.closest('.mms-string-color-selector .mms-string-swatch');
      if (swatch) {
        // Stop event propagation to prevent triggering image picker
        e.stopPropagation();
        e.preventDefault();
        
        const selector = swatch.closest('.mms-string-color-selector');
        const magnetId = parseInt(selector.dataset.magnet);
        const color = swatch.dataset.color;
        
        if (magnetId && color) {
          // Update UI
          selector.querySelectorAll('.mms-string-swatch').forEach(s => s.classList.remove('active'));
          swatch.classList.add('active');
          selector.dataset.stringColor = color;
          
          // Update state
          this.updateStringColor(magnetId, color);
        }
      }
    });
    
    // Event delegation for string color swatches in editing workspace
    document.addEventListener('click', (e) => {
      const swatch = e.target.closest('#mms-string-color-controls .mms-string-swatch');
      if (swatch) {
        // Stop event propagation to prevent triggering other handlers
        e.stopPropagation();
        e.preventDefault();
        
        const color = swatch.dataset.color;
        const state = this.store.getState();
        const activeMagnetId = state.ui.activeMagnetId;
        
        if (activeMagnetId && color) {
          // Update UI
          const stringColorControls = document.getElementById('mms-string-color-controls');
          if (stringColorControls) {
            stringColorControls.querySelectorAll('.mms-string-swatch').forEach(s => s.classList.remove('active'));
            swatch.classList.add('active');
          }
          
          // Update state
          this.updateStringColor(activeMagnetId, color);
        }
      }
    });
  }

  /**
   * Update string color for a specific magnet/ornament
   * 
   * Updates the magnet state with the new string color and syncs UI
   * across both preview panel and editing workspace.
   * 
   * @param {number} magnetId - The ID of the magnet/ornament (1-5)
   * @param {string} color - The string color ('red', 'green', or 'gold')
   */
  updateStringColor(magnetId, color) {
    const state = this.store.getState();
    const magnet = state.magnets[magnetId];
    if (magnet) {
      this.store.dispatch(currentState => ({
        ...currentState,
        magnets: {
          ...currentState.magnets,
          [magnetId]: {
            ...magnet,
            stringColor: color
          }
        }
      }));
      
      // Sync UI in preview panel
      const selector = document.querySelector(`.mms-string-color-selector[data-magnet="${magnetId}"]`);
      if (selector) {
        selector.dataset.stringColor = color;
        selector.querySelectorAll('.mms-string-swatch').forEach(s => {
          s.classList.toggle('active', s.dataset.color === color);
        });
      }
      
      // Sync UI in editing workspace if this magnet is active
      if (state.ui.activeMagnetId === magnetId) {
        const stringColorControls = document.getElementById('mms-string-color-controls');
        if (stringColorControls) {
          stringColorControls.querySelectorAll('.mms-string-swatch').forEach(s => {
            s.classList.toggle('active', s.dataset.color === color);
          });
        }
      }
    }
  }

  /**
   * Handle variant select input changes
   */
  handleVariantSelectChange() {
    // Small delay to allow variant data to update
    setTimeout(() => this.handleVariantScriptChange(), 100);
  }

  /**
   * Handle variant script content changes
   */
  handleVariantScriptChange() {
    const variantScript = document.querySelector('variant-selects [data-selected-variant]');
    if (!variantScript) return;
    
    try {
      const variant = JSON.parse(variantScript.textContent);
      const magnetCount = this.extractMagnetCount(variant);
      this.updateMagnetCards(magnetCount);
    } catch (e) {
      console.warn('Could not parse updated variant data:', e);
    }
  }

  /**
   * Render function to update UI based on state changes.
   * @param {object} state The current application state.
   */
  render(state) {
            MMS_LOGGER.debug('State updated:', state);
    // Delegate rendering tasks to components.
    this.magnetCards.forEach(card => card.render(state.magnets[card.id]));
    this.workspace.render(state);
  }

  // Add a method to PersonalizerApp to enable/disable editing controls:
  toggleEditingLocked(locked) {
    // Disable/enable all editing controls (delete, upload, variant change)
    this.magnetCards.forEach(card => {
      card.setEditingLocked?.(locked);
    });
    // Disable all variant selectors, even if dynamically rendered
    const variantSelects = document.querySelectorAll('variant-selects select, .product__variants select, select[name^="id"], select[data-variant-option]');
    variantSelects.forEach(sel => sel.disabled = !!locked);
    
    // Disable string color swatches in editing workspace for ornaments
    if (this.isOrnament) {
      const stringColorControls = document.getElementById('mms-string-color-controls');
      if (stringColorControls) {
        const swatches = stringColorControls.querySelectorAll('.mms-string-swatch');
        swatches.forEach(swatch => {
          swatch.disabled = !!locked;
          swatch.style.opacity = locked ? '0.6' : '1';
          swatch.style.cursor = locked ? 'not-allowed' : 'pointer';
        });
      }
    }
  }
}

// === PERSONALIZATION READY CHECK ===

/**
 * Checks if all required magnets have images uploaded (state === 'settled').
 * Returns true if ready, false otherwise.
 */
function checkPersonalizationReady(store) {
  // Defensive programming: handle undefined store
  if (!store) {
    console.warn('[checkPersonalizationReady] Store is undefined');
    return false;
  }
  
  try {
    const state = store.getState();
    if (!state) {
      console.warn('[checkPersonalizationReady] Store state is undefined');
      return false;
    }
    
    const count = state.product?.magnetCount || 0;
    if (!state.ui?.isLocked) return false;
    
    const isOrnament = window.personalizerApp?.isOrnament || false;
    for (let i = 1; i <= count; i++) {
      const magnet = state.magnets?.[i];
      if (!magnet || magnet.state !== 'settled') {
        return false;
      }
      // For ornaments, verify string color is set (defensive check - defaults to 'gold')
      if (isOrnament && !magnet.stringColor) {
        console.warn(`[checkPersonalizationReady] Magnet ${i} missing stringColor for ornament`);
        return false;
      }
    }
    return true;
  } catch (error) {
    console.error('[checkPersonalizationReady] Error checking personalization readiness:', error);
    return false;
  }
}

/**
 * Enables or disables the Add to Cart button based on personalization state.
 */
function updateAddToCartButton(store) {
          MMS_LOGGER.debug('[updateAddToCartButton] Starting update...');
  
  // Check if all magnets are uploaded
  const state = store.getState();
  const magnetCount = state.product?.magnetCount || 0;
  let allUploaded = true;
  
          MMS_LOGGER.debug('[updateAddToCartButton] Magnet count:', magnetCount);
  
  for (let i = 1; i <= magnetCount; i++) {
    const magnet = state.magnets?.[i];
            MMS_LOGGER.debug(`[updateAddToCartButton] Magnet ${i} state:`, magnet?.state);
    if (!magnet || magnet.state !== 'settled') {
      allUploaded = false;
      break;
    }
  }
  
          MMS_LOGGER.debug('[updateAddToCartButton] All uploaded:', allUploaded);
  

  
  // Update cart actions section visibility (restored separate approach)
  const cartActionsSection = document.querySelector('[id^="MmsCartActions-"]');
          MMS_LOGGER.debug('[updateAddToCartButton] Cart actions section found:', !!cartActionsSection);
  if (cartActionsSection) {
    const ready = checkPersonalizationReady(store);
            MMS_LOGGER.debug('[updateAddToCartButton] Personalization ready:', ready);
    cartActionsSection.style.display = ready ? 'block' : 'none';
            MMS_LOGGER.debug('[updateAddToCartButton] Cart actions section display set to:', cartActionsSection.style.display);
    
    // Attach submit listener when section becomes visible
    if (ready) {
              MMS_LOGGER.debug('[updateAddToCartButton] Attaching submit listener to cart actions form');
      attachPersonalizerFormSubmitListener(store);
    }
  } else {
    console.warn('[updateAddToCartButton] Cart actions section not found in DOM');
  }
  
  // Update lock toggle visibility
  const lockToggleDiv = document.getElementById('personalization-lock-toggle');
  const infoLabelDiv = document.getElementById('personalization-info-label');
          MMS_LOGGER.debug('[updateAddToCartButton] Lock toggle div found:', !!lockToggleDiv);
        MMS_LOGGER.debug('[updateAddToCartButton] Info label div found:', !!infoLabelDiv);
  
  if (lockToggleDiv) {
            MMS_LOGGER.debug('[updateAddToCartButton] Setting lock toggle display to:', allUploaded ? 'block' : 'none');
    lockToggleDiv.style.display = allUploaded ? 'block' : 'none';
    
    // Show/hide info label along with toggle
    if (infoLabelDiv) {
      infoLabelDiv.style.display = allUploaded ? 'block' : 'none';
    }
    
    // Update toggle state based on isLocked
    const checkbox = document.getElementById('personalization-lock-checkbox');
    if (checkbox) {
      const isLocked = state.ui?.isLocked || false;
              MMS_LOGGER.debug('[updateAddToCartButton] Setting checkbox checked to:', isLocked);
      checkbox.checked = isLocked;
      
      // Update aria-checked for accessibility
      const slider = checkbox.nextElementSibling;
      if (slider && slider.getAttribute('role') === 'switch') {
        slider.setAttribute('aria-checked', isLocked.toString());
      }
    }
  } else {
    console.warn('[updateAddToCartButton] Lock toggle div not found in DOM');
  }
  
  // Legacy button update (for backward compatibility)
  const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
  if (productForm) {
    const sectionId = productForm.closest('[id^="MmsCartActions-"]')?.id?.replace('MmsCartActions-', '');
    if (sectionId) {
      const buttonId = `ProductSubmitButton-${sectionId}`;
      const button = document.getElementById(buttonId);
      if (button) {
        const ready = checkPersonalizationReady(store);
        button.disabled = !ready;
        MMS_LOGGER.debug('[updateAddToCartButton] Legacy button found and updated:', buttonId, 'disabled:', !ready);
      } else {
        MMS_LOGGER.debug('[updateAddToCartButton] Legacy button not found:', buttonId);
      }
    }
  }
}



// Global debug function for console access
window.debugPersonalizationState = function() {
  if (!window.personalizerApp || !window.personalizerApp.store) {
            MMS_LOGGER.debug('❌ Personalizer app not initialized');
    return;
  }
  
  const store = window.personalizerApp.store;
  const state = store.getState();
  
          MMS_LOGGER.debug('🔍 Personalization State Debug:');
          MMS_LOGGER.debug('Store:', store);
        MMS_LOGGER.debug('State:', state);
        MMS_LOGGER.debug('Product magnet count:', state.product?.magnetCount);
        MMS_LOGGER.debug('UI locked:', state.ui?.isLocked);
  
  // Check each magnet
  for (let i = 1; i <= state.product?.magnetCount; i++) {
    const magnet = state.magnets?.[i];
            MMS_LOGGER.debug(`Magnet ${i}:`, magnet ? `state=${magnet.state}` : 'undefined');
  }
  
  // Check cart actions section
  const cartActionsSection = document.querySelector('[id^="MmsCartActions-"]');
          MMS_LOGGER.debug('Cart actions section found:', !!cartActionsSection);
  if (cartActionsSection) {
            MMS_LOGGER.debug('Cart actions section display:', cartActionsSection.style.display);
  }
  
  // Check personalization controls
  const toggle = document.getElementById('personalization-lock-toggle');
  const checkbox = document.getElementById('personalization-lock-checkbox');
          MMS_LOGGER.debug('Personalization toggle found:', !!toggle);
        MMS_LOGGER.debug('Personalization checkbox found:', !!checkbox);
  
  // Check personalization ready
  const ready = checkPersonalizationReady(store);
          MMS_LOGGER.debug('Personalization ready:', ready);
  
  return ready;
};

// Instantiate the main app to start the personalizer
        MMS_LOGGER.debug('🚀 MMS Personalizer Script Loaded - v2024.1.16.7 - VARIANT MAGNET COUNT FIXED + UPLOAD REGRESSION FIXED');
window.personalizerApp = new PersonalizerApp();

// === MutationObserver to keep Add to Cart button in sync ===
(function observeAddToCartButton() {
  const productSection = document.querySelector('[data-section]');
  if (!productSection) return;
  const sectionId = productSection.getAttribute('data-section');
  if (!sectionId) return;
  const targetNode = productSection;

  const observer = new MutationObserver((mutationsList) => {
            MMS_LOGGER.debug('[MutationObserver] Detected DOM mutation:', mutationsList);
    if (window.personalizerApp && window.personalizerApp.store) {
      updateAddToCartButton(window.personalizerApp.store);
    }
  });

  observer.observe(targetNode, { childList: true, subtree: true });
          MMS_LOGGER.debug('[MutationObserver] Started observing product section for DOM changes');
})();

// Form submission is now handled by attachPersonalizerFormSubmitListener function

// Helper to attach submit listener robustly
function attachPersonalizerFormSubmitListener(store) {
  // Try to find the form in the mms-cart-actions section first
  let form = document.querySelector('[id^="MmsCartActions-"] form[data-type="add-to-cart-form"]');
  
  // Fallback to the old product-form structure
  if (!form) {
    const productFormEl = document.querySelector('product-form');
    if (productFormEl) {
      form = productFormEl.querySelector('form');
    }
  }
  
  if (!form) {
    console.warn('[MMS Personalizer] No add-to-cart form found');
    return;
  }
  
  // Prevent duplicate listeners
  if (form._personalizerListenerAttached) {
            MMS_LOGGER.debug('[MMS Personalizer] Submit listener already attached to form');
    return;
  }
  
  form._personalizerListenerAttached = true;
          MMS_LOGGER.debug('[MMS Personalizer] Attaching submit event listener to form:', form.id || form.className);
  
  form.addEventListener('submit', async function(event) {
            MMS_LOGGER.debug('[MMS Personalizer] Submit handler triggered');
    const store = window.personalizerApp.store;
    if (!store) {
      console.error('[MMS Personalizer] Store not found!');
      return false;
    }
            MMS_LOGGER.debug('[MMS Personalizer] Store found, checking personalization readiness...');
    const isReady = checkPersonalizationReady(store);
            MMS_LOGGER.debug(`[MMS Personalizer] Personalization ready check: ${isReady}`);
    if (!isReady) {
      event.preventDefault();
      event.stopImmediatePropagation();
      alert('Please upload all images and lock edits before adding to cart.');
      return false;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    
    // Update variant ID to match the selected magnet pack variant
    const variantInput = form.querySelector('input[name="id"]');
    const selectedVariantId = document.querySelector('#mms-variant-form input[name="id"]')?.value;
    if (variantInput && selectedVariantId) {
      variantInput.value = selectedVariantId;
      MMS_LOGGER.debug('[MMS Personalizer] Updated variant ID to:', selectedVariantId);
    } else {
      console.warn('[MMS Personalizer] Could not find variant ID to update');
    }
    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.style.opacity = '0.6';
    }
    const state = store.getState();
    const personalizedData = [];
    for (let i = 1; i <= state.product.magnetCount; i++) {
      const magnet = state.magnets[i];
      if (magnet && magnet.state === 'settled' && magnet.personalizedImage) {
        const itemData = {
          position: i,
          imageUrl: magnet.personalizedImage,
          orientation: magnet.orientation || 'landscape', // Corrected default
        };
        // Add string color for ornaments
        if (window.personalizerApp?.isOrnament) {
          itemData.stringColor = magnet.stringColor || 'gold';
          MMS_LOGGER.debug(`[MMS Personalizer] Magnet ${i} stringColor: ${itemData.stringColor}`);
        }
        personalizedData.push(itemData);
      }
    }
    MMS_LOGGER.debug(`[MMS Personalizer] Personalized data:`, personalizedData);
            MMS_LOGGER.debug(`[MMS Personalizer] Found ${personalizedData.length} personalized images to upload.`);
    if (personalizedData.length === 0) {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.style.opacity = '1';
      }
      alert('No personalized images found.');
      return false;
    }
    try {
              MMS_LOGGER.debug('[MMS Personalizer] Starting Cloudflare upload...');
      const CLOUDFLARE_WORKER_URL = 'https://api.mymagneticstories.com';
      const images = personalizedData.map(item => item.imageUrl);
      const metadata = personalizedData.map(item => ({
        placeholderIndex: item.position,
        orientation: item.orientation,
        stringColor: item.stringColor, // Add string color to metadata
        timestamp: new Date().toISOString(),
        version: '1.0.0',
        personalizations: {},
        fabricState: null,
        isPersonalized: true
      }));
      const response = await fetch(CLOUDFLARE_WORKER_URL + '/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify({ images, metadata })
      });
              MMS_LOGGER.debug(`[MMS Personalizer] Cloudflare response status: ${response.status}`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      const result = await response.json();
              MMS_LOGGER.debug('[MMS Personalizer] Cloudflare upload successful, adding properties...');
      result.results.forEach((uploadResult, idx) => {
        if (uploadResult.cdnUrl) {
          const urlInput = document.createElement('input');
          urlInput.type = 'hidden';
          urlInput.name = `properties[_Personalized Image ${uploadResult.placeholderIndex}]`;
          urlInput.value = uploadResult.cdnUrl;
          form.appendChild(urlInput);

          const thumbInput = document.createElement('input');
          thumbInput.type = 'hidden';
          thumbInput.name = `properties[_Personalized Thumbnail ${uploadResult.placeholderIndex}]`;
          thumbInput.value = uploadResult.variants && uploadResult.variants[0] ? uploadResult.variants[0] : uploadResult.cdnUrl;
          form.appendChild(thumbInput);

          const originalData = personalizedData.find(d => d.position === uploadResult.placeholderIndex);
          
          // Add string color as separate line item property (for ornaments)
          if (window.personalizerApp?.isOrnament && originalData?.stringColor) {
            const stringColorInput = document.createElement('input');
            stringColorInput.type = 'hidden';
            stringColorInput.name = `properties[_String Color ${uploadResult.placeholderIndex}]`;
            stringColorInput.value = originalData.stringColor;
            form.appendChild(stringColorInput);
          }

          // <<< START: THIS IS THE FIX >>>
          const detailsInput = document.createElement('input');
          detailsInput.type = 'hidden';
          detailsInput.name = `properties[_Personalized Details ${uploadResult.placeholderIndex}]`;
          
          const details = {
            orientation: originalData?.orientation || 'landscape',
            stringColor: originalData?.stringColor || 'gold' // Add string color to details
          };
          
          detailsInput.value = JSON.stringify(details);
          form.appendChild(detailsInput);
          // <<< END: THIS IS THE FIX >>>
        }
      });
              MMS_LOGGER.debug('[MMS Personalizer] Properties added, submitting form...');
      // Remove all event listeners by cloning and replacing the form
      const newForm = form.cloneNode(true);
      form.parentNode.replaceChild(newForm, form);
      // Submit the new form
      newForm.submit();
      // Ensure no other scripts can run
      return false;
    } catch (error) {
      console.error('[MMS Personalizer] Error during upload:', error);
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.style.opacity = '1';
      }
      alert('Failed to upload personalized images. Please try again.');
      return false;
    }
  }, true);
}

// Form submission listeners are attached when the add-to-cart section becomes visible

// Global function to manually show toggle for debugging
window.showToggle = function() {
  const toggle = document.getElementById('personalization-lock-toggle');
  if (toggle) {
            MMS_LOGGER.debug('🔧 Manually showing toggle');
    toggle.style.display = 'block';
    return true;
  } else {
            MMS_LOGGER.debug('❌ Toggle element not found in DOM');
    return false;
  }
};

// Global debug function for console access